import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData, BLANK_SYMBOL_ID } from "../../../SymbolData";
import { Settings, GAME_LAYOUT } from "../../../settings";
import { COIN_SYMBOL_IDS, COIN_SYMBOL_ID } from "../../../SymbolData";
import { cosmeticCoinValue, coinValueAt } from "../../Shared/coinValues";
import { wearCoinValue } from "../../Shared/coinValueLabel";
import store from "../../../State/Store/store";
import { formatToCustomLocale } from "../../../utilities";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { fsDiag } from "../../../utils/fsDiag";
import RevealSymbolController from "../../Shared/RevealSymbolController";
import GoldenMysteryCellController from "../../Shared/GoldenMysteryCellController";
import {
  buildGoldenMysteryConfig,
  buildGoldenMysterySpine,
  buildFeatureSpine,
  // One table for the coin's value tier, shared with the Golden Mystery so the
  // slot a number is worn in cannot drift between the two.
  // NOT USED since the coin wears one slot (coinValueLabel):
  // coinSkinForValue,
} from "../../Main/Mystery/goldenMysteryConfig";
import {
  buildGambleScatterConfig,
  toStripItem as gambleToStripItem,
  toStaticCell as gambleToStaticCell,
} from "../../Main/GambleScatter/gambleScatterConfig";
import {
  buildPlusScatterConfig,
  toStripItem as plusToStripItem,
  toStaticCell as plusToStaticCell,
} from "../../Main/GambleScatter/plusScatterConfig";
import {
  SCATTER_2_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
  MYSTERY_SYMBOL_ID,
  GOLDEN_FRAME_SYMBOL_ID,
  symbolData as revealSymbolData,
} from "../../../SymbolData";

// Reused for the symbol-centre lookup below — one per module, never nested.
const _levelTrailPoint = new PIXI.Point();

// Manages overlay animations for winning symbols with pre-positioned spine instances


/**
 * Symbols that run a reveal sequence instead of a single animation clip.
 *
 * Kept here rather than in a module of its own: it is a three-line lookup, and
 * this controller is its only reader. The sequences themselves are symbol DATA
 * in SymbolData — SC1 is the exception and has a builder, because it composes a
 * strip, a mask and a landed overlay from the cell size.
 *
 *   buildConfig(cellW, cellH)  the RevealSymbolController config
 *   toStripItem(result)        the round's payload as a strip item
 *   toStaticCell(item)         static texture the settled cell keeps, or null
 */
const REVEAL_SYMBOLS = {
  // ── The `+FS` scatter reveals THROUGH SC1's window ──
  //
  // Free spins only. The cell lands the golden scatter, its doors open, and a
  // strip of +1/+2/+3 spins inside — the same sequence SC1 runs, with a
  // different strip. See plusScatterConfig.
  //
  // `hostSymbolId` is what makes that possible: the pooled spine this reveal
  // drives is SC1's `scg`, not this symbol's own `bs`. Everything else — the
  // config, the strip, the settled cell — is this entry's.
  [SCATTER_3_SYMBOL_ID]: {
    hostSymbolId: SCATTER_2_SYMBOL_ID,
    buildConfig: buildPlusScatterConfig,
    toStripItem: plusToStripItem,
    toStaticCell: plusToStaticCell,
  },

  // SC1 — lands, opens, spins the free-spin packages inside itself, settles on
  // the one the backend awarded.
  [SCATTER_2_SYMBOL_ID]: {
    buildConfig: buildGambleScatterConfig,
    toStripItem: gambleToStripItem,
    toStaticCell: gambleToStaticCell,
  },

  // B1 — lands, builds, opens onto a symbol the backend chose before the screen
  // arrived. Nothing to spin, so no strip, and nothing INJECTED either: the
  // mystery is a lid over the real symbol, and opening it is the whole reveal.
  // The host fades out and is handed straight back.
  //
  // The config is the SAME OBJECT the base game reads — `symbolData[9].reveal`
  // — so the two games cannot disagree about what a mystery does. Which also
  // means a change there lands here on the same commit, ported or not.
  //
  // toStaticCell is null because there is no picture to leave behind. The cell
  // underneath was painted with the revealed symbol before the doors parted —
  // see the mystery block in useFreeSpinBasicWinController.
  [MYSTERY_SYMBOL_ID]: {
    buildConfig: () => ({
      ...revealSymbolData[MYSTERY_SYMBOL_ID].reveal,
    }),
    // The replacement id, straight through — it is what the doors open onto.
    toStripItem: (replacementId) => replacementId,
    toStaticCell: () => null,
  },
};

class FreeSpinBasicOverlayReelController {
  constructor(
    overlaySymbolRefs,
    reelIndex,
    symbolWidth,
    symbolHeight,
    reelContainer = null,
    bet = 1,
  ) {
    this.overlaySymbolRefs = overlaySymbolRefs;
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelContainer = reelContainer;
    this.bet = bet || 1; // Store bet value for coin calculations
    this.prePositionedSpines = new Map();
    this.activeAnimations = new Map();

    // ── Landings still in the air, and who is waiting for them ──
    //
    // The base game's mechanism, copied verbatim — see
    // Games/Main/OverlayReel/OverlayReelController. Both the win presentation
    // and the next spin take TRACK 0, the same track a landing bounce plays on,
    // so either arriving early cuts it off. Keyed by symbolIndex, so a cell
    // that lands twice replaces its own entry; a cell with no landing clip
    // never registers, which is what makes a reel with no animations at all a
    // non-case. See waitForLandings.
    this._pendingLandings = new Set();
    this._landingWaiters = [];
    this.animationCache = new Map();

    // ✅ NEW: cache of available animations per spineKey
    this.animationAvailability = new Map();

    // Internal caches for performance optimization
    this._spineScaleCache = new Map(); // Cache for calculateSpineScale results
    this._expandingWildScaleCache = null; // Cache for expanding wild scale
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;
    this._coinDisplayValueCache = new Map(); // Cache for coin display values

    // Expanding wild full-reel spine properties
    this.expandingWildSpine = null;
    this.expandingWildSpineData = {
      skeleton: "wild_expand-skeleton",
      atlas: "wild_expand-atlas",
    };
    this.expandingWildTimeouts = [];
    this.isExpandingWildVisible = false;
    this.expandingWildInitialized = false;
    this.currentExpandingWildAnimation = null; // Track current animation ("appear", "idle", or "win")

    // Spine scaling configuration
    this.expandingWildHeightMultiplier = 1.0;
    this.expandingWildYOffsetMultiplier = 0;

    // Coin value text management (attached to spine bone)
    this.coinValueContainers = new Map(); // symbolIndex → { counterContainer, bitmapTexts, displayValue }

    // The settled gamble scatter standing on the board the round opened with,
    // if any: { symbolIndex, tier, value }. One per reel at most — there is
    // only ever one SC1. See showSettledGambleScatter.
    this._settledScatter = null;

    // Track pending requestAnimationFrame handles for cleanup
    this._pendingRAF = null;

    this.initializeAllSpineInstances();

    // Initialize and warm up expanding wild spine on FreeSpinBasicReel 0 only (shader compilation is global)
    // This prevents first-time RAF warnings when expanding wild appears
    if (this.reelIndex === 0 && this.reelContainer) {
      this.initializeExpandingWildSpine();
    }
  }

  getShadowReelController() {
    return this;
  }

  /**
   * Get the expanding wild spine instance for event listening
   * @returns {Spine|null} The expanding wild spine instance
   */
  getExpandingWildSpine() {
    return this.expandingWildSpine;
  }

  /**
   * Calculate display value for coin symbols (coinValue × bet)
   * @param {number} symbolId - Symbol ID
   * @param {number|null} betOverride - Optional bet value for error recovery (ONE TIME use)
   */
  /**
   * The coin's OWN value, before the bet — what the skin tier is keyed on.
   *
   * Split out so the tier and the printed label come from ONE number. They used
   * to disagree by construction: the label came from here and the slot was
   * hardcoded, so the number could be worn in a tier that did not match the art.
   *
   * It is 0 in this game, and that is not a bug to fix here. `symbolData[13]`
   * carries `value: null` on purpose — "coin values come from the API per cell,
   * never from here" — so nothing on this path knows a cell's value, and
   * _getCoinDisplayValue returns "" for the same reason. The moment a per-cell
   * value is plumbed in, the tier follows it without another edit. Until then
   * coinSkinForValue(0) is `coin1`, whose slot sits on the same bone as the
   * other three, so the placement is right either way.
   */
  _getCoinValue(symbolId) {
    const raw = symbolData[symbolId]?.value;
    const value = parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  _getCoinDisplayValue(symbolId, betOverride = null) {
    const symbolValue = symbolData[symbolId]?.value;
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use bet override if provided (error recovery), otherwise use this.bet
      const bet = betOverride !== null ? betOverride : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Create coin value text container with BitmapText
   * Implements progressive scaling based on digit count:
   * - 1-4 digits: Normal size (no scaling)
   * - 5-8 digits: Horizontal squishing only (preserve height)
   * - 9+ digits: Uniform scaling (both width and height)
   */
  createCoinValueText(displayValue) {
    if (!displayValue || displayValue === "") {
      return null;
    }

    const counterContainer = new PIXI.Container();
    counterContainer.name = "coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.visible = true;
    counterContainer.zIndex = 1000; // Render above spine effects

    // Calculate font size based on current symbol height (same as FreeSpinBasicReelController).
    // The ticker counteracts the spine's PIXI scale, so this fontSize produces
    // identical visual results to the texture-based text.
    const fontSize =
      this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    // Create BitmapText for the value (target native size: 34px)
    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font_secondary_scaled",
      fontSize: fontSize,
    });

    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;
    bitmapText.zIndex = 1;
    bitmapText.roundPixels = true;

    // Force update to get accurate measurements
    bitmapText.updateText();

    // Calculate available width (with padding)
    const coinWidth = this.symbolWidth;
    const paddingPercent = 0.12; // 12% padding on each side
    const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

    const actualTextWidth = bitmapText.width;

    // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
    if (actualTextWidth > maxTextWidth) {
      const scale = maxTextWidth / actualTextWidth;
      bitmapText.scale.set(scale);
    }

    counterContainer.addChild(bitmapText);

    return { counterContainer, bitmapTexts: [bitmapText] };
  }

  /**
   * Show coin value text for a coin symbol
   * @param {number} symbolIndex - Position index
   * @param {number} symbolId - Symbol ID
   * @param {object} spineInstance - Spine instance
   * @param {number|null} betOverride - Optional bet value for error recovery
   */
  showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride = null) {
    // Skip entirely when coins don't appear on base game reels
    if (!FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS) return;

    // Check if it's a coin symbol
    if (!COIN_SYMBOL_IDS.includes(symbolId)) {
      return;
    }

    // Check if coin value text already exists for this symbol at this position
    const existingCoinData = this.coinValueContainers.get(symbolIndex);
    // if (
    //   existingCoinData &&
    //   existingCoinData.counterContainer &&
    //   !existingCoinData.counterContainer.destroyed
    // ) {
    //   // Coin value text already exists, don't recreate it
    //   return;
    // }
    // ── Kept only when it is THIS spine's label with THIS round's number ──
    // Keyed by cell alone, a label left behind by a hide path that skips
    // hideCoinValueText (hideLandingAnimation, spines hidden from outside)
    // was kept on the next round: the spine showed the old number while the
    // picture showed the new one (Vladimir, 2026-09-30). A round with no
    // number here (cosmetic) keeps whatever is worn — no re-roll flicker.
    const roundCoinValue = coinValueAt(
      store.getState().freeSpins?.currentRound,
      this.reelIndex,
      symbolIndex,
    );
    if (
      existingCoinData &&
      existingCoinData.counterContainer &&
      !existingCoinData.counterContainer.destroyed &&
      existingCoinData.spineInstance === spineInstance &&
      (roundCoinValue == null || existingCoinData.displayValue === roundCoinValue)
    ) {
      return;
    }

    this.hideCoinValueText(symbolIndex);

    // ── Cleopatra's Fire: the round's number, on the coin's own slot ──
    // As the base overlay reel: the free-spin round's value for this cell
    // (round.coinValues), a cosmetic one only when it carries none; plain
    // coins only. The old Zeus body below drew a BitmapText this game has no
    // font for, on slots the `c` rig does not have.
    if (symbolId !== COIN_SYMBOL_ID || !spineInstance) return;
    // const value =
    //   coinValueAt(store.getState().freeSpins?.currentRound, this.reelIndex, symbolIndex) ??
    //   cosmeticCoinValue(betOverride ?? undefined);
    const value = roundCoinValue ?? cosmeticCoinValue(betOverride ?? undefined);
    const worn = wearCoinValue(spineInstance, value);
    if (!worn) return;
    this.coinValueContainers.set(symbolIndex, {
      counterContainer: worn.label,
      bitmapTexts: [],
      spineInstance,
      slotName: worn.slotName,
      displayValue: value,
    });

    // NOT USED — the Zeus body (BitmapText on txt_slot_coin1-4):
    // // Hide existing coin value text if any (cleanup destroyed containers)
    // this.hideCoinValueText(symbolIndex);
    //
    // // Calculate display value (use bet override for recovery, otherwise current bet)
    // const displayValue = this._getCoinDisplayValue(symbolId, betOverride);
    // if (!displayValue || displayValue === "") {
    //   return;
    // }
    //
    // // Create coin value text
    // const textData = this.createCoinValueText(displayValue);
    // if (!textData) {
    //   return;
    // }
    //
    // const { counterContainer, bitmapTexts } = textData;
    //
    // // Store reference with spine instance for proper cleanup
    // this.coinValueContainers.set(symbolIndex, {
    //   counterContainer,
    //   bitmapTexts,
    //   spineInstance, // Needed for removeSlotObject on cleanup
    //   displayValue, // Store the value so it never changes during animation
    // });
    //
    // // Attach to the animated txt_slot_value slot.
    // // Spine-pixi's updateSlotObject overwrites the slot object's entire
    // // localTransform matrix with the bone's world transform (bone.a, bone.d)
    // // every frame. Two scale factors shrink the text:
    // //   1. spine.scale.x (PIXI scale to fit symbol in grid) — e.g. 0.405
    // //   2. bone.a (bone's world transform scale) — e.g. ~0.5
    // // We counter both by scaling fontSize up by 1/(spineScale * boneBaseScale).
    // // The bone's animated scale change (idle pulse 1.0→1.1) still applies
    // // naturally on top, giving the text its desired pulse/win effects.
    // if (spineInstance) {
    //   const spineScale = spineInstance.scale.x || 1;
    //
    //   // Force world transform update so bone values are computed (they're 0 before first frame)
    //   spineInstance.update(0);
    //   const valueBone = spineInstance.skeleton?.findBone("value");
    //   const boneBaseScale = Math.abs(valueBone?.a) || 1;
    //
    //   // Total counter-scale: undo both spine PIXI scale and bone world scale
    //   const totalScale = spineScale * boneBaseScale;
    //   if (totalScale && totalScale !== 0) {
    //     bitmapTexts.forEach((bt) => {
    //       if (bt && !bt.destroyed) {
    //         bt.fontSize = bt.fontSize / totalScale;
    //         bt.updateText();
    //       }
    //     });
    //
    //     // Re-check auto-fit with adjusted measurements (in spine-local units)
    //     const coinWidth = this.symbolWidth;
    //     const paddingPercent = 0.12;
    //     const maxTextWidth = (coinWidth * (1 - paddingPercent * 2)) / totalScale;
    //     bitmapTexts.forEach((bt) => {
    //       if (bt && !bt.destroyed) {
    //         bt.scale.set(1); // Reset before measuring
    //         bt.updateText();
    //         if (bt.width > maxTextWidth) {
    //           bt.scale.set(maxTextWidth / bt.width);
    //         }
    //       }
    //     });
    //   }
    //
    //   // ── The slot, resolved from the rig rather than assumed ──
    //   //
    //   // This asked for "txt_slot_value" for a long time, and `c` has not had a
    //   // slot by that name since it was re-exported with skins coin1-coin4 and a
    //   // matching txt_slot_coin1-4. spine-pixi's getSlotFromRef THROWS on a name
    //   // it cannot find, so the only reason this never blew up is that
    //   // COIN_VALUES_ON_REELS is false and the method returns on its first line.
    //   //
    //   // Resolved the same way the Golden Mystery resolves it — coinSkinForValue,
    //   // one table for both — so the slot always matches the skin the coin is
    //   // wearing. All four hang off bone `value`, so the geometry is identical
    //   // whichever tier it is; picking the matching one just keeps it honest.
    //   //
    //   // Guarded, for the same reason wearCellNumber guards: a rig without the
    //   // slot must cost the number, never the round.
    //   const slotName = `txt_slot_${coinSkinForValue(this._getCoinValue(symbolId))}`;
    //   if (!spineInstance.skeleton?.findSlot?.(slotName)) {
    //     console.warn(
    //       "[FreeSpinBasicOverlayReel] No coin value slot in the rig:",
    //       slotName,
    //     );
    //     this.hideCoinValueText(symbolIndex);
    //     return;
    //   }
    //
    //   spineInstance.addSlotObject(slotName, counterContainer);
    //
    //   // Store spine reference for cleanup. The SLOT NAME goes with it — remove
    //   // has to use the same one add did, and hardcoding it there was the other
    //   // half of the same bug.
    //   const stored = this.coinValueContainers.get(symbolIndex);
    //   stored.spineInstance = spineInstance;
    //   stored.slotName = slotName;
    // }
  }

  /**
   * Hide coin value text for a symbol
   */
  hideCoinValueText(symbolIndex) {
    const coinData = this.coinValueContainers.get(symbolIndex);
    if (!coinData) {
      return;
    }

    // Detach from spine slot BEFORE destroying (prevents localTransform error)
    if (coinData.spineInstance && coinData.counterContainer && !coinData.counterContainer.destroyed) {
      try {
        coinData.spineInstance.removeSlotObject(
          // The name add used. Never a literal — see showCoinValueText.
          coinData.slotName,
          coinData.counterContainer,
        );
      } catch {
        // Silent — spine may already be destroyed
      }
    }

    // Destroy bitmap texts
    if (coinData.bitmapTexts) {
      coinData.bitmapTexts.forEach((text) => {
        if (text && !text.destroyed) {
          text.destroy();
        }
      });
    }

    // Destroy container
    if (coinData.counterContainer && !coinData.counterContainer.destroyed) {
      if (coinData.counterContainer.parent) {
        coinData.counterContainer.parent.removeChild(coinData.counterContainer);
      }
      coinData.counterContainer.destroy({ children: true });
    }

    // Remove from map
    this.coinValueContainers.delete(symbolIndex);
  }

  /**
   * Hide all coin value texts
   */
  hideAllCoinValueTexts() {
    const symbolIndices = Array.from(this.coinValueContainers.keys());
    symbolIndices.forEach((symbolIndex) => {
      this.hideCoinValueText(symbolIndex);
    });
  }

  /**
   * Update bet value and refresh all visible coin value texts
   */
  updateBet(newBet) {
    const oldBet = this.bet;
    this.bet = newBet || 1; // Store new bet value for next spin

    // Invalidate coin display value cache when bet changes
    if (oldBet !== this.bet) {
      this._coinDisplayValueCache.clear();
    }

    // Don't update coin values on reels - they belong to a completed spin round
    // Coin values will update when new symbols land on next spin
    // Note: Paytable/info overlay update separately (not affected by this)
    return;
  }

  /**
   * ✅ Combined and optimized method for checking available animations.
   * Returns object with hasLanding, hasIdle, and hasAppear using cached data.
   */
  getAvailableAnimations(symbolIndex, symbolId) {
    // ── A settled Golden cell has no animations, by definition ──
    //
    // The landing hook asks this and then acts on the answer: `hasIdle` means
    // "play the idle and HIDE the flat texture". Refusing the idle inside
    // showIdleAnimation is not enough — the hook hides the texture anyway, and
    // the finished composite underneath goes with it. A cell that should have
    // been a photograph became a blank.
    //
    // Answering honestly instead sends the hook down its own else branch:
    // hideWinAnimation, then showSymbolTexture. Which is precisely right — the
    // picture is on the reel and nothing should move.
    //
    // `_goldenStatic` ONLY, never `hasGoldenMystery`. A live Golden Mystery
    // lands as an ordinary mystery and needs its landing clip; its controller
    // does not exist until after that. Widening this to the controllers would
    // take the bounce off every golden cell in the room.
    if (this._goldenStatic?.has(symbolIndex)) {
      return { hasLanding: false, hasIdle: false, hasAppear: false };
    }

    // Texture-only game optimization - skip landing/idle checks if disabled
    if (
      !FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED &&
      !FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED
    ) {
      return { hasLanding: false, hasIdle: false, hasAppear: true };
    }

    // Always look up using position 0 since all positions share the same symbol's animations
    const warmupKey = `0_${symbolId}`;
    const available = this.animationAvailability.get(warmupKey);

    if (!available) {
      return { hasLanding: false, hasIdle: false, hasAppear: false };
    }

    let hasLanding = false;
    let hasIdle = false;
    let hasAppear = false;

    for (const name of available) {
      // Landing clip names vary per spine: "in", "land", "landing", "bounce".
      // Matched lower-case. "in" is exact-only — as a substring it would match
      // "win" and every symbol would claim a landing animation.
      const lower = name.toLowerCase();
      if (
        !hasLanding &&
        (lower.includes("land") || lower.includes("bounce") || lower === "in")
      ) {
        hasLanding = true;
      }
      // Treat "static" as an idle-equivalent (e.g. jackpot spine j.json has no "idle" but has "static")
      if (!hasIdle && (name.includes("idle") || name === "static")) hasIdle = true;
      if (!hasAppear && name.includes("appear")) hasAppear = true;
      if (hasLanding && hasIdle && hasAppear) break;
    }

    // Apply feature flags to results
    return {
      hasLanding:
        FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED && hasLanding,
      hasIdle: FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED && hasIdle,
      hasAppear,
    };
  }

  // ✅ hasIdleAnimationAt now uses cached availability instead of scanning animations
  hasIdleAnimationAt(symbolIndex, symbolId) {
    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    return hasIdle;
  }

  // Show win animation using pre-positioned spine instances.
  // If the spine has no actual win animation (e.g. Zeus bonus symbols), falls back
  // to idle so the symbol still looks alive rather than playing a wrong/fallback clip.
  // @param {number|null} betOverride - Optional bet value for error recovery
  // @param {number} targetLoops - Number of loops (0=infinite, 1=play once and hold, N=play N times and hold)
  showWinAnimation(
    symbolIndex,
    symbolId,
    animationType = "win",
    betOverride = null,
    targetLoops = 0,
  ) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    // ── The Golden Mystery owns this cell ──
    //
    // Its frame is the pooled `my` spine, and the cell's sprite still claims
    // symbol 9 — the board handed to the reel was masked and a B2 cell is
    // deliberately never given a new identity, because the feature decides what
    // it pays. So every ordinary presentation call for this row resolves OUR
    // FRAME, and the first thing most of them do is setBonesToSetupPose — which
    // for the mystery is the doors SHUT, over a spinning track.
    //
    // The feature runs its own presentation from first frame to last. Nothing
    // else may touch the cell until spin start hands it back.
    //
    // ── But it DOES celebrate, and only it knows how ──
    //
    // Refusing is no longer the end of it. The cell owns its whole picture —
    // frame, lid and outcome — so it is ASKED to celebrate rather than driven,
    // and it plays `my.win` on its own track with the light chase still running
    // underneath. See GoldenMysteryCellController.playWin.
    //
    // A RELEASED cell has no controller and does nothing, which is right: it
    // handed its spine back at settle and is a flat texture now.
    if (this.hasGoldenMystery(symbolIndex)) {
      const cell = this.goldenControllers?.get(symbolIndex);
      // Woken first: between lines the cell is PARKED and the reel's flat copy
      // is standing in for it. See setCellVisible.
      cell?.setCellVisible?.(true);
      // The line's own loop count, so this cell animates for exactly as long as
      // every other symbol on it.
      cell?.playWin?.(targetLoops);
      return;
    }

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const existingAnimation = this.activeAnimations.get(symbolIndex);

    // Early return if SAME symbol and animation type already playing
    // Prevents flicker on shared symbols across multiple winning lines during cycling
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === animationType
    ) {
      // Symbol already playing - don't restart! Just ensure visibility
      spineInstance.alpha = 1;
      spineInstance.visible = true;
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
      return;
    }

    // Also skip restart when existing is "idle" from a win-to-idle fallback
    // and the incoming request would also produce the same fallback.
    // Without this, symbols without a dedicated win clip restart every cycle
    // while symbols with win clips stay stable (dedup above catches them).
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === "idle" &&
      (animationType === "win" || animationType === "win_fast")
    ) {
      // Check if this request would also fall back to idle
      const candidateAnim = this.selectSpineAnimation(spineInstance, animationType, symbolId);
      if (candidateAnim && !candidateAnim.toLowerCase().includes("win")) {
        // Same fallback — don't restart, just ensure visibility
        spineInstance.alpha = 1;
        spineInstance.visible = true;
        overlaySymbol.alpha = 1;
        overlaySymbol.visible = true;
        return;
      }
    }

    // Only hide if DIFFERENT symbol or animation type
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    let selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId,
    );

    // Win-to-idle fallback: if we asked for "win" but the resolved animation
    // doesn't actually contain "win" (selectSpineAnimation fell back to a
    // generic clip), play idle instead so the symbol still looks correct.
    // This handles Zeus bonus symbols whose spines have no dedicated win clip.
    let effectiveAnimationType = animationType;
    if (
      animationType === "win" &&
      selectedAnimation &&
      !selectedAnimation.toLowerCase().includes("win")
    ) {
      const idleAnimation = this.selectSpineAnimation(spineInstance, "idle", symbolId);
      if (idleAnimation) {
        selectedAnimation = idleAnimation;
        effectiveAnimationType = "idle";
      }
    }



    const shouldLoop = targetLoops === 0 || targetLoops > 1;
    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      const entry = spineInstance.state.addAnimation(0, selectedAnimation, shouldLoop, 0);

      // For finite loops > 1, count completions and stop looping at target
      if (targetLoops > 1 && entry) {
        let completedLoops = 0;
        entry.listener = {
          complete: () => {
            completedLoops++;
            if (completedLoops >= targetLoops) {
              entry.loop = false; // Hold on last frame
            }
          },
        };
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame to apply so the skeleton pose matches the new animation
    // before any pixel renders. Without this, setup/stale pose shows for 1 frame.
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: effectiveAnimationType,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }


  /**
   * Sync/restart win animation to frame 0 without hiding (keeps animations in sync across lines).
   * If the spine has no actual win animation, falls back to idle (same logic as showWinAnimation).
   * Unlike showWinAnimation, this ALWAYS restarts even if same animation is playing.
   * @param {number} targetLoops - Number of loops (0=infinite, 1=play once and hold, N=play N times and hold)
   */
  syncWinAnimation(
    symbolIndex,
    symbolId,
    animationType = "win",
    betOverride = null,
    targetLoops = 0,
  ) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    // ── The Golden Mystery owns this cell ──
    //
    // Its frame is the pooled `my` spine, and the cell's sprite still claims
    // symbol 9 — the board handed to the reel was masked and a B2 cell is
    // deliberately never given a new identity, because the feature decides what
    // it pays. So every ordinary presentation call for this row resolves OUR
    // FRAME, and the first thing most of them do is setBonesToSetupPose — which
    // for the mystery is the doors SHUT, over a spinning track.
    //
    // The feature runs its own presentation from first frame to last. Nothing
    // else may touch the cell until spin start hands it back.
    //
    // ── But it DOES celebrate, and only it knows how ──
    //
    // Refusing is no longer the end of it. The cell owns its whole picture —
    // frame, lid and outcome — so it is ASKED to celebrate rather than driven,
    // and it plays `my.win` on its own track with the light chase still running
    // underneath. See GoldenMysteryCellController.playWin.
    //
    // A RELEASED cell has no controller and does nothing, which is right: it
    // handed its spine back at settle and is a flat texture now.
    if (this.hasGoldenMystery(symbolIndex)) {
      const cell = this.goldenControllers?.get(symbolIndex);
      // Woken first: between lines the cell is PARKED and the reel's flat copy
      // is standing in for it. See setCellVisible.
      cell?.setCellVisible?.(true);
      // The line's own loop count, so this cell animates for exactly as long as
      // every other symbol on it.
      cell?.playWin?.(targetLoops);
      return;
    }

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    // Reset bones before starting new animation (clears stale scale from prior anim)
    if (spineInstance.skeleton?.setBonesToSetupPose) {
      spineInstance.skeleton.setBonesToSetupPose();
    }

    // Ensure visibility
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    let selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId,
    );

    // Win-to-idle fallback: same logic as showWinAnimation.
    // If selectSpineAnimation fell back to a non-win clip, play idle instead.
    let effectiveAnimationType = animationType;
    if (
      animationType === "win" &&
      selectedAnimation &&
      !selectedAnimation.toLowerCase().includes("win")
    ) {
      const idleAnimation = this.selectSpineAnimation(spineInstance, "idle", symbolId);
      if (idleAnimation) {
        selectedAnimation = idleAnimation;
        effectiveAnimationType = "idle";
      }
    }

    const shouldLoop = targetLoops === 0 || targetLoops > 1;

    // Reset animation to frame 0 without hiding
    if (spineInstance.state && selectedAnimation) {
      // Get current track (if exists)
      const currentTrack = spineInstance.state.tracks[0];

      // If already playing the same animation, just reset time
      if (currentTrack && currentTrack.animation.name === selectedAnimation) {
        currentTrack.trackTime = 0;
        currentTrack.loop = shouldLoop;
        // Re-attach loop control listener for finite loops
        if (targetLoops > 1) {
          let completedLoops = 0;
          currentTrack.listener = {
            complete: () => {
              completedLoops++;
              if (completedLoops >= targetLoops) {
                currentTrack.loop = false;
              }
            },
          };
        } else {
          currentTrack.listener = null;
        }
      } else {
        // Different animation, need to switch
        spineInstance.state.clearTracks();
        const entry = spineInstance.state.addAnimation(0, selectedAnimation, shouldLoop, 0);

        if (targetLoops > 1 && entry) {
          let completedLoops = 0;
          entry.listener = {
            complete: () => {
              completedLoops++;
              if (completedLoops >= targetLoops) {
                entry.loop = false;
              }
            },
          };
        }
      }
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: effectiveAnimationType,
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // ✅ Show landing animation using cached availability
  // @param {number|null} betOverride - Optional bet value for error recovery
  showLandingAnimation(symbolIndex, symbolId, betOverride = null) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const spineKey = `${symbolIndex}_${symbolId}`;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    const { hasLanding, hasIdle } = this.getAvailableAnimations(
      symbolIndex,
      symbolId,
    );

    if (!hasLanding && !hasIdle) {
      return;
    }

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    if (hasLanding && hasIdle) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );

      if (spineInstance.state && landingAnimation && idleAnimation) {
        spineInstance.state.clearTracks();
        this._trackLanding(
          symbolIndex,
          spineInstance.state.addAnimation(0, landingAnimation, false, 0),
        );
        // Round-level idle: landing holds on last frame; idle triggers after ALL reels stop
        if (!FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {
          spineInstance.state.addAnimation(0, idleAnimation, true, 0);
        } else {

        }
      }
    } else if (hasIdle) {
      // Round-level idle: idle-only symbols stay as static texture until round-level trigger
      if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {

        // Reset spine visibility — don't activate the overlay
        spineInstance.alpha = 0.001;
        spineInstance.autoUpdate = false;
        overlaySymbol.alpha = 0;
        return;
      }
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );
      if (spineInstance.state && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, idleAnimation, true, 0);
      }
    } else if (hasLanding) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        this._trackLanding(
          symbolIndex,
          spineInstance.state.addAnimation(0, landingAnimation, true, 0),
        );
        if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {

        }
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame — use same time step (1/60) as texture extraction
    // so the overlay's initial pose exactly matches the static texture.
    spineInstance.state?.update(1 / 60);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;


    // When round-level idle is active and we have landing, record as "landing" (not "idle")
    // so the post-spin logic knows landing is still holding and can trigger idle explicitly
    const isRoundLevelHolding = FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE && hasLanding;
    const recordedType = isRoundLevelHolding
      ? "landing"
      : hasLanding && hasIdle ? "idle" : hasIdle ? "idle" : "landing";

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: recordedType,
      hadLandingAnimation: hasLanding,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Show landing animation followed by win (instead of idle).
   * Used for Zeus bonus symbols — they play land→win during POT charging.
   * @param {number} symbolIndex
   * @param {number} symbolId
   * @param {number|null} betOverride
   */
  showLandingToWinAnimation(symbolIndex, symbolId, betOverride = null) {
    const spineKey = `${symbolIndex}_${symbolId}`;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasLanding } = this.getAvailableAnimations(symbolIndex, symbolId);

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    const winAnimation = this.selectSpineAnimation(spineInstance, "win", symbolId);

    if (hasLanding && winAnimation) {
      const landingAnimation = this.selectSpineAnimation(spineInstance, "land", symbolId);
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, false, 0);
        spineInstance.state.addAnimation(0, winAnimation, true, 0);
      }
    } else if (winAnimation) {
      if (spineInstance.state) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, winAnimation, true, 0);
      }
    }

    // Make visible AFTER animation is queued
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "win",
    });

    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // Show idle animation (unchanged, uses cache internally now)
  // @param {number|null} betOverride - Optional bet value for error recovery
  // @param {boolean} frozen - If true, sets up idle pose but doesn't animate (autoUpdate=false).
  //   Used during per-reel landing so the swap from texture→spine happens during the
  //   reel bounce (invisible). Call unfreezeIdleAnimations() later to start playing.
  showIdleAnimation(symbolIndex, symbolId, betOverride = null, frozen = false) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    // If the same idle is already playing and spine is visible, don't restart.
    // This prevents phase-reset flicker when multiple callers trigger idle on
    // the same symbol in the same frame.
    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === "idle" &&
      spineInstance.alpha >= 0.5
    ) {
      // If already showing frozen and caller wants unfrozen, just unfreeze
      if (!frozen && !spineInstance.autoUpdate) {
        spineInstance.autoUpdate = true;
      }
      return;
    }

    // Reset bone transforms (scale, position, rotation) to clear stale values
    // from prior animations (e.g. win animation may scale the symbol).
    // Always reset bones — idle doesn't key every bone so stale transforms carry over.
    // Only reset SLOTS when hidden — when visible (e.g. landing → idle),
    // slot attachments like shadows must be preserved (idle inherits them).
    // Skip on first-ever use: spine is already in setup pose.
    const wasPreviouslyUsed = existingAnimation != null;
    if (wasPreviouslyUsed && spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId,
    );
    if (spineInstance.state && idleAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.setAnimation(0, idleAnimation, true);
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame to apply so the skeleton pose matches the new animation
    // before any pixel renders. Without this, setup/stale pose shows for 1 frame.
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    // When frozen (autoUpdate=false), the ticker won't call update(), so mesh
    // vertices would never be recomputed. Force a zero-dt update now so the
    // static pose renders correctly.
    if (frozen) {
      spineInstance.autoUpdate = false;
      spineInstance.update(0);
    }
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = !frozen;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Unfreezes all frozen idle animations on this reel.
   * Called when all reels have stopped to start the idle movement.
   */
  unfreezeIdleAnimations() {
    for (const [, entry] of this.activeAnimations) {
      if (entry.animationType === "idle" && entry.spineInstance) {
        entry.spineInstance.autoUpdate = true;
      }
    }
  }

  /**
   * Smoothly crossfade from the current animation (e.g. win) to idle.
   * Uses setAnimation with a mix duration so Spine blends poses — no snap.
   * Forces frame 0 via update(0)+apply() so all symbols start in phase.
   */
  transitionToIdle(symbolIndex, symbolId) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    // Ensure visible and updating
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId,
    );
    if (spineInstance.state && idleAnimation) {
      // Full clean reset — clearTracks removes stale animation data that Spine's
      // mix system would otherwise blend from (causing a visible bump).
      spineInstance.state.clearTracks();
      if (spineInstance.skeleton?.setToSetupPose) {
        spineInstance.skeleton.setToSetupPose();
      }
      spineInstance.state.setAnimation(0, idleAnimation, true);
      spineInstance.state.update(0);
      spineInstance.state.apply(spineInstance.skeleton);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });
  }

  // Show appear animation (used when symbol first appears, may fallback to idle or win)
  // @param {number|null} betOverride - Optional bet value for error recovery
  showAppearAnimation(symbolIndex, symbolId, betOverride = null) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasAppear, hasIdle, hasLanding } = this.getAvailableAnimations(
      symbolIndex,
      symbolId,
    );

    // If no animations available, don't show anything
    if (!hasAppear && !hasIdle && !hasLanding) return;

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    let selectedAnimation = null;

    // Try appear first, fallback to idle, then landing
    if (hasAppear) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        symbolId,
      );
    } else if (hasIdle) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );
    } else if (hasLanding) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
    }

    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
    }

    // Make visible AFTER animation is queued
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  hideWinAnimation(symbolIndex) {
    // The Golden Mystery owns this cell — see showWinAnimation. Everything
    // below is wrong for it, but the celebration it was asked for still has to
    // END, or the cell keeps bouncing after its line has moved on.
    //
    // Its own track is cleared, never the cell's: track 0 is the light chase,
    // and stopping that would take the cell dark for the rest of the round.
    if (this.hasGoldenMystery(symbolIndex)) {
      // PARKED, not just stopped. A live Golden cell draws above the dim, so a
      // cell that merely stopped bouncing would stay lit through every other
      // line's turn — the coins still glowing while the scatters have theirs.
      // Parked, the reel's identical composite shows through and dims.
      //
      // Through parkGoldenCell, which refuses while the feature still owns the
      // cell. This is reachable mid-sequence, and parking there would stop the
      // spine ticking and hang the whole feature.
      this.parkGoldenCell(symbolIndex);
      return;
    }

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.visible = false;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;
    overlaySymbol.visible = false;

    // Spine is invisible (alpha 0.001) — safe to do a full reset.
    // Reset bones AND slots so the spine is pristine for the next animation.
    // Without this, bone transforms (scale/position) from the win animation
    // persist and corrupt the next animation shown on this spine.
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);

    // Hide coin value text if any
    this.hideCoinValueText(symbolIndex);
  }

  /**
   * Register a landing clip so waitForLandings can wait for it.
   * The base game's twin — keep the two identical.
   *
   * @param {number} symbolIndex
   * @param {object} entry The spine TrackEntry the clip was queued on.
   */
  _trackLanding(symbolIndex, entry) {
    if (!entry) return;
    this._pendingLandings.add(symbolIndex);
    entry.listener = {
      complete: () => this._landingSettled(symbolIndex),
      // Ended without completing — cleared by the next spin, or by a win taking
      // the track. Nothing is waiting for it any more either way.
      end: () => this._landingSettled(symbolIndex),
    };
  }

  /** One landing is done, however it ended. Releases the waiters at zero. */
  _landingSettled(symbolIndex) {
    if (!this._pendingLandings.delete(symbolIndex)) return;
    if (this._pendingLandings.size === 0) this._releaseLandingWaiters();
  }

  _releaseLandingWaiters() {
    if (!this._landingWaiters.length) return;
    const waiters = this._landingWaiters;
    this._landingWaiters = [];
    for (const resolve of waiters) resolve();
  }

  /**
   * Resolves once every landing clip on this reel has finished, and IMMEDIATELY
   * when none is playing. Never rejects; the caller owns the stall guard.
   *
   * @returns {Promise<void>}
   */
  waitForLandings() {
    if (this._pendingLandings.size === 0) return Promise.resolve();
    return new Promise((resolve) => this._landingWaiters.push(resolve));
  }

  hideLandingAnimation(symbolIndex) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    if (animationData.animationType !== "landing") return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.visible = false;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;
    overlaySymbol.visible = false;

    // Spine is invisible (alpha 0.001) — safe to do a full reset.
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);
  }

  hideAllAnimations(exceptPositions = null) {
    // Nothing may outlive this — called at spin start and on cleanup. A landing
    // still registered belongs to a round that is over, and a waiter holding on
    // it would stall the NEXT round. See the base game's twin.
    this._pendingLandings.clear();
    this._releaseLandingWaiters();

    // First: hide tracked animations (the fast path)
    const activeIndexes = Array.from(this.activeAnimations.keys());
    activeIndexes.forEach((symbolIndex) => {
      if (exceptPositions && exceptPositions.has(symbolIndex)) {
        return;
      }
      this.hideWinAnimation(symbolIndex);
    });

    // ── Golden cells are invisible to the loop above ──
    //
    // It walks `activeAnimations`, and a Golden cell has no entry there:
    // playGoldenMysterySequence deletes it so nothing fights the feature for
    // track 0. So nothing here ever told one to stop, and the coins kept
    // celebrating straight through the scatters' line.
    //
    // Same contract as the loop: a cell named in `exceptPositions` is the line
    // about to play and is left alone.
    for (const symbolIndex of this.goldenControllers?.keys() || []) {
      if (exceptPositions?.has(symbolIndex)) continue;
      // Through parkGoldenCell, never straight at the cell: it holds the gate
      // that keeps a sweep from freezing a sequence still in flight.
      this.parkGoldenCell(symbolIndex);
    }

    // Hide all coin value texts (they should already be hidden by hideWinAnimation, but ensure cleanup)
    this.hideAllCoinValueTexts();

    // CRITICAL: Full sweep of ALL pre-positioned spines to catch orphaned autoUpdate=true
    // Without this, spines that lost their activeAnimations entry (race conditions in async win flow)
    // remain ticking on every frame forever, causing gradual CPU accumulation in turbo mode
    if (!exceptPositions) {
      for (const [key, spine] of this.prePositionedSpines.entries()) {
        if (!spine || !spine.autoUpdate) continue;

        // ── A cell that is deliberately busy is not an orphan ──
        //
        // This sweep catches spines still ticking with no activeAnimations
        // entry to explain them. A reveal looks exactly like one, because
        // taking a cell over DELETES that entry so nothing fights it for
        // track 0. Sweeping it mid-sequence sets autoUpdate false and clears
        // the track, so the `complete` the sequence is awaiting can never
        // arrive and the round hangs holding criticalAnimationPlaying.
        //
        // Keyed on _revealHold rather than on revealControllers, which would
        // be far too wide: SC1's controller is never released — nothing calls
        // destroyRevealSymbols outside cleanup() — so that cell would be
        // exempt for the rest of the session, ticking for ever and leaving its
        // open window over whatever lands there next. _revealHold covers
        // exactly the window where sweeping would break something.
        const symbolIndex = Number(key.split("_")[0]);
        if (this._revealHold?.has(symbolIndex)) continue;
        // ── A Golden cell is not an orphan either ──
        //
        // Its frame spine ticks with no activeAnimations entry to explain it,
        // for exactly the same reason a reveal's does — so this sweep found it
        // and hid it outright, alpha and all. The cell vanished mid-round and
        // the feature's own controller went on driving a picture nobody could
        // see. The loop above owns when a Golden cell is parked; this must not
        // second-guess it.
        if (this.goldenControllers?.has(symbolIndex)) continue;

        spine.autoUpdate = false;
        spine.alpha = 0.001;
        spine.visible = false;
        if (spine.state) {
          spine.state.clearTracks();
        }
      }
      this.activeAnimations.clear();
    }
  }

  hideAllAnimationsExcept(upcomingPositions = []) {
    if (!upcomingPositions || upcomingPositions.length === 0) {
      this.hideAllAnimations();
      return;
    }

    // Golden cells are counted too — they hold no activeAnimations entry, so
    // testing that alone would leave a reel of them lit for the next line.
    if (this.activeAnimations.size === 0 && !this.goldenControllers?.size) {
      return;
    }

    const preserveMap = new Map();
    for (let i = 0; i < upcomingPositions.length; i++) {
      const [reelIndex, symbolIndex, symbolId] = upcomingPositions[i];
      if (reelIndex === this.reelIndex) {
        preserveMap.set(symbolIndex, symbolId);
      }
    }

    for (const [symbolIndex, animationData] of this.activeAnimations) {
      const preservedSymbolId = preserveMap.get(symbolIndex);
      if (preservedSymbolId !== animationData.symbolId) {
        this.hideWinAnimation(symbolIndex);
      }
    }

    // ── And the Golden cells, on the ROW alone ──
    //
    // The loop above keeps a cell only when the upcoming line wants the same
    // SYMBOL there. That test cannot be applied here: a Golden cell's sprite
    // claims symbol 9 whatever is standing in it, so comparing ids would park
    // every one of them on every line. The row being named is the whole test.
    for (const symbolIndex of this.goldenControllers?.keys() || []) {
      if (preserveMap.has(symbolIndex)) continue;
      this.parkGoldenCell(symbolIndex);
    }
  }

  calculateSpineScale(
    spineInstance,
    containerWidth,
    containerHeight,
    skipAnimationSetup = false,
    symbolId = null,
  ) {
    if (
      !skipAnimationSetup &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
    }

    // Per-category scale — single lookup with DEFAULT fallback
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';
    const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[symbolPay]
      ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
      ?? 1;

    // Check cache
    const cacheKey = `${containerWidth}_${containerHeight}_${symbolPay}`;
    if (this._spineScaleCache.has(cacheKey)) {
      return this._spineScaleCache.get(cacheKey);
    }

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const scale = (containerHeight / nativeHeight) * categoryScale;

    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    // No centering offset needed: the sprite uses anchor(0.5) at position (0,0),
    // and the spine's origin (0,0) is its design center. Both align at the
    // container origin by default. A non-zero offset here would create a visible
    // shift between the static texture and the overlay spine during transitions.
    const x = 0;
    const y = 0;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache.set(cacheKey, result);

    return result;
  }

  resizeAnimations(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    // Invalidate caches when dimensions change
    this._spineScaleCache.clear();
    this._expandingWildScaleCache = null;
    this._coinDisplayValueCache.clear(); // Clear coin cache on resize (bet might have changed)
    this._lastSymbolWidth = newSymbolWidth;
    this._lastSymbolHeight = newSymbolHeight;

    const nativeHeight = Settings.SYMBOL_HEIGHT;

    for (const [key, spineInstance] of this.prePositionedSpines.entries()) {
      if (spineInstance?.scale) {
        // Extract symbolId from key format "${symbolIndex}_${symbolId}"
        const symbolId = parseInt(key.split('_')[1], 10);
        const info = symbolData[symbolId];
        const sPay = info?.symbolPay || '';
        const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[sPay]
          ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
          ?? 1;
        const symbolScale = (newSymbolHeight / nativeHeight) * categoryScale;
        spineInstance.scale.set(symbolScale);
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol) {
        overlaySymbol.y = newSymbolHeight * i;
      }
    }

    // Update coin value text fontSize on resize.
    // Must apply same totalScale counter (spine PIXI scale × bone world scale)
    // as showCoinValueText, since both factors change on resize.
    this.coinValueContainers.forEach((coinData) => {
      if (coinData.bitmapTexts && coinData.bitmapTexts.length > 0) {
        // Compute totalScale from spine + bone (same logic as showCoinValueText)
        let totalScale = 1;
        if (coinData.spineInstance) {
          const spineScale = coinData.spineInstance.scale.x || 1;
          coinData.spineInstance.update(0);
          const valueBone = coinData.spineInstance.skeleton?.findBone("value");
          const boneBaseScale = Math.abs(valueBone?.a) || 1;
          totalScale = spineScale * boneBaseScale;
        }

        const baseFontSize =
          this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const adjustedFontSize = totalScale !== 0 ? baseFontSize / totalScale : baseFontSize;

        coinData.bitmapTexts.forEach((bitmapText) => {
          if (bitmapText && !bitmapText.destroyed) {
            bitmapText.fontSize = adjustedFontSize;
            bitmapText.scale.set(1);
            bitmapText.updateText();

            // Recalculate uniform scaling if text overflows
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = totalScale !== 0
              ? (coinWidth * (1 - paddingPercent * 2)) / totalScale
              : coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = bitmapText.width;
            if (actualTextWidth > maxTextWidth) {
              const scale = maxTextWidth / actualTextWidth;
              bitmapText.scale.set(scale);
            }
          }
        });
      }
    });

    // Resize expanding wild spine if initialized
    if (this.expandingWildInitialized) {
      this.applyExpandingWildScaling();
    }
  }

  createSpineInstance(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo?.spine || symbolInfo.spine === "") return null;

    const spineName = symbolInfo.spine;
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: false,
      });

      // Apply skin if feature is enabled and symbol specifies one (shared spine with multiple skins)
      if (
        FEATURES_CONFIG.SPINE_ANIMATIONS.SHARED_SPINE_SKINS &&
        symbolInfo.skin &&
        spine.skeleton
      ) {
        spine.skeleton.setSkinByName(symbolInfo.skin);
        spine.skeleton.setSlotsToSetupPose();
      }

      return spine;
    } catch {
      return null;
    }
  }

  selectSpineAnimation(spineInstance, animationType, symbolId) {
    const cacheKey = `${symbolId}_${animationType}`;
    let cachedAnimation = this.animationCache.get(cacheKey);
    if (cachedAnimation !== undefined) return cachedAnimation;

    if (!spineInstance.skeleton?.data?.animations) {
      this.animationCache.set(cacheKey, null);
      return null;
    }

    const animations = spineInstance.skeleton.data.animations;
    let selectedAnimation = null;

    if (animationType === "auto") {
      selectedAnimation = animations[0]?.name || null;
    } else {
      const exactMatch = animations.find((anim) => {
        const name = anim.name.toLowerCase();
        const type = animationType.toLowerCase();
        // For "win" type, exclude "win_fast" to prevent false matches
        if (type === "win") {
          return name.includes("win") && !name.includes("win_fast");
        }
        return name.includes(type);
      });
      if (exactMatch) {
        selectedAnimation = exactMatch.name;
      } else if (animationType === "win_fast") {
        // win_fast not found → fall back to regular "win"
        const winFallback = animations.find((anim) =>
          anim.name.toLowerCase().includes("win"),
        );
        selectedAnimation = winFallback?.name || animations[0]?.name || null;
      } else if (animationType === "idle") {
        // "static" is the idle-equivalent for spines that don't have an explicit "idle" animation
        // (e.g. jackpot spine j.json: ['land', 'static', 'win'])
        const staticAnim = animations.find((anim) => anim.name === "static");
        selectedAnimation = staticAnim?.name || animations[0]?.name || null;
      } else if (animationType === "land") {
        // The include-match above already caught "land" and "landing". These
        // are the other names the animators use for the same clip. Same rule
        // as getAvailableAnimations: "in" exact-only, or it matches "win".
        const landAlias = animations.find((anim) => {
          const name = anim.name.toLowerCase();
          return name.includes("bounce") || name === "in";
        });
        selectedAnimation = landAlias?.name || animations[0]?.name || null;
      } else {
        selectedAnimation = animations[0]?.name || null;
      }
    }

    this.animationCache.set(cacheKey, selectedAnimation);
    return selectedAnimation;
  }

  _smoothTransitionToAnimation(
    spineInstance,
    newAnimationType,
    symbolId,
    loop = true,
  ) {
    if (!spineInstance || !spineInstance.state) return false;

    const newAnimation = this.selectSpineAnimation(
      spineInstance,
      newAnimationType,
      symbolId,
    );
    if (!newAnimation) return false;

    try {
      const currentTracks = spineInstance.state.tracks;
      const hasCurrentAnimation =
        currentTracks && currentTracks.length > 0 && currentTracks[0];

      if (hasCurrentAnimation) {
        const currentTrack = currentTracks[0];
        const currentAnimationName = currentTrack.animation
          ? currentTrack.animation.name
          : null;

        if (currentAnimationName === newAnimation) return true;
        spineInstance.state.addAnimation(0, newAnimation, loop, 0.1);
      } else {
        spineInstance.state.setAnimation(0, newAnimation, loop);
      }
      return true;
    } catch {
      return false;
    }
  }

  async initializeAllSpineInstances() {
    this.createAllPrePositionedSpines();

    // Force-upload all unique symbol atlas textures to GPU.
    // Deferred to next frame so globalThis.__PIXI_RENDERER__ is available.
    // Only run once from the first reel to avoid redundant uploads.
    if (this.reelIndex === 0) {
      requestAnimationFrame(() => {
        const uniqueSpineNames = [
          ...new Set(
            Object.values(symbolData)
              .map((s) => s.spine)
              .filter((s) => s && s !== "")
          ),
        ];
        SpineAtlasUploader.uploadAll(uniqueSpineNames);
      });
    }
  }

  createAllPrePositionedSpines() {
    const WARMUP_POSITION = 0; // Only warm up in first symbol position
    const WARMUP_REEL = 0; // Only warm up in first reel

    for (
      let symbolIndex = 0;
      symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
      symbolIndex++
    ) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) continue;

      for (const symbolId of Object.keys(symbolData).map(Number)) {
        // Conditionally exclude blank symbols for Hold and Win mode
        // BLANK_SYMBOL_ID (0): In base game = wild (w1), in Hold and Win = blank space
        const shouldExcludeBlank =
          FEATURES_CONFIG.HOLD_AND_WIN?.ENABLED && symbolId === BLANK_SYMBOL_ID;

        if (shouldExcludeBlank) {
          continue;
        }

        const symbolInfo = symbolData[symbolId];
        // Early check: skip if symbol doesn't exist or has no valid spine
        if (
          !symbolInfo ||
          !symbolInfo.spine ||
          symbolInfo.spine === null ||
          symbolInfo.spine === "" ||
          typeof symbolInfo.spine !== "string"
        ) {
          continue;
        }

        // A texture-only symbol has no business up here. It carries a `spine`
        // purely so its static texture can be captured; the reel layer owns it.
        // Building a skeleton for it adds a second one to the cell, and because
        // it is created last it paints over the spine already there.
        if (symbolInfo.textureOnly) {
          continue;
        }

        let spineInstance;

        // Only create and warm up spine in first reel, first position
        if (this.reelIndex === WARMUP_REEL && symbolIndex === WARMUP_POSITION) {
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true,
            symbolId,
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          // ── The golden FRAME sits UNDER the mystery lid ──
          //
          // Same container, and PIXI draws equal zIndex in insertion order —
          // which puts id 21 above id 9 simply because it is numbered higher.
          // The lid has to be on top: it is what opens, and the frame is what
          // it uncovers.
          spineInstance.zIndex =
            symbolId === GOLDEN_FRAME_SYMBOL_ID ? 9 : 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // ✅ Build animation availability cache from warmed-up spine
          const key = `${symbolIndex}_${symbolId}`;
          if (spineInstance.skeleton?.data?.animations?.length > 0) {
            const available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase(),
              ),
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(key, available);
            }
          }

          // Warm up animations (shader compilation + GPU buffer upload happens here)
          // Order matters: each setAnimation replaces the previous animation's meshes.
          // "win" is warmed LAST so its meshes survive in the display list after cleanup.
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");

          // Schedule cleanup after GPU has rendered all animation meshes (3 frames)
          this.scheduleWarmupCleanup(spineInstance, symbolIndex);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        } else {
          // For all other reels and positions, create fresh instances (shaders already compiled)
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true,
            symbolId,
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          // ── The golden FRAME sits UNDER the mystery lid ──
          //
          // Same container, and PIXI draws equal zIndex in insertion order —
          // which puts id 21 above id 9 simply because it is numbered higher.
          // The lid has to be on top: it is what opens, and the frame is what
          // it uncovers.
          spineInstance.zIndex =
            symbolId === GOLDEN_FRAME_SYMBOL_ID ? 9 : 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // ✅ Build animation availability cache from this spine instance
          // All positions share the same symbol's animations, so use position 0 key for lookup
          const warmupKey = `${WARMUP_POSITION}_${symbolId}`;

          // Check if we already have the cache entry for this symbol (from position 0)
          let available = this.animationAvailability.get(warmupKey);

          // If not, populate it from this spine instance
          if (
            !available &&
            spineInstance.skeleton?.data?.animations?.length > 0
          ) {
            available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase(),
              ),
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(warmupKey, available);
            }
          }

          // Warm up GPU mesh buffers for all animation types (prevents first-use jitter)
          // "win" is warmed LAST so its meshes survive after cleanup
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");
          this.scheduleWarmupCleanup(spineInstance, symbolIndex);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        }
      }
    }
  }

  warmUpSpineAnimation(spineInstance, symbolId, animationType = "win") {
    if (!spineInstance?.state) return;
    try {
      const animation = this.selectSpineAnimation(
        spineInstance,
        animationType,
        symbolId,
      );
      if (!animation) return;

      // Use setAnimation (replaces current) instead of clearTracks + addAnimation.
      // This preserves the previous animation's mesh data until the new one replaces it,
      // giving the GPU a chance to render each animation's mesh before it's replaced.
      spineInstance.state.setAnimation(0, animation, false);
      spineInstance.state.update(0.016);
      spineInstance.skeleton.updateWorldTransform();

      spineInstance.alpha = 0.001;
      const overlaySymbol = spineInstance.parent;
      if (overlaySymbol) overlaySymbol.alpha = 0.001;
    } catch {}
  }

  /**
   * Finalize warmup cleanup after GPU has had time to render all animation meshes.
   * Called once per spine AFTER all warmUpSpineAnimation calls complete.
   */
  scheduleWarmupCleanup(spineInstance, symbolIndex = null) {
    if (!spineInstance?.state) return;

    const overlaySymbol = spineInstance.parent;

    // Wait 3 frames — ensures PIXI's renderer has drawn the spine mesh
    // and uploaded vertex/texture data to the GPU for all warmed-up animations
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // ── Do NOT park a cell that has been TAKEN OVER since ──
          //
          // These three frames are a race, and it only became a losing one when
          // the overlay stopped being mounted at boot. Built at boot, the park
          // landed during loading and nothing could possibly have claimed a
          // cell yet. Built when the room opens — which is what mounting the
          // board with the room means — the settled gamble card is shown INSIDE
          // this window, and the park then hides the very spine it was just
          // put on. The card stayed alive and correct underneath, lighting and
          // clearing its pips, on a host nobody could see.
          //
          // Guarded on the same two collections the sweep in hideAllAnimations
          // uses, and for the same reason: a cell that is deliberately busy is
          // not an idle warm-up.
          if (
            symbolIndex !== null &&
            (this._revealHold?.has(symbolIndex) ||
              this.activeAnimations?.has(symbolIndex))
          ) {
            return;
          }

          if (spineInstance.state) {
            // Clear tracks after GPU has rendered all warmed-up meshes (3 frames).
            // Mesh GPU buffers are already cached — clearTracks() just resets track state
            // so the first showWinAnimation() starts from a clean slate.
            spineInstance.state.clearTracks();
            spineInstance.autoUpdate = false;
          }
          spineInstance.alpha = 0.001;
          spineInstance.visible = false;
          if (overlaySymbol) {
            overlaySymbol.alpha = 0.001;
            overlaySymbol.visible = false;
          }
        });
      });
    });
  }

  forceDeepSpineCleanup() {
    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance?.state) {
        spineInstance.state.clearTracks();
        spineInstance.autoUpdate = false;
        spineInstance.alpha = 0.001;
        spineInstance.visible = false;
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol?.alpha > 0) {
        overlaySymbol.alpha = 0.001;
        overlaySymbol.visible = false;
      }
    }

    this.activeAnimations.clear();
  }

  /**
   * Show appear animation at a specific position (used for cascading expanding wilds)
   * @param {number} symbolIndex - Position index (0-3)
   * @param {number} targetSymbolId - Symbol ID to show (typically wild = 0)
   */
  showAppearAnimationAtPosition(symbolIndex, targetSymbolId) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    // Get the pre-positioned spine instance for this position with target symbol
    const spineKey = `${symbolIndex}_${targetSymbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    // Hide any existing animation at this position
    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    // Make spine and overlay visible
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    // Play appear animation (fallback to land if appear not available)
    let appearAnimation = this.selectSpineAnimation(
      spineInstance,
      "appear",
      targetSymbolId,
    );

    // If no appear animation found, try land as fallback
    if (
      !appearAnimation ||
      appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name
    ) {
      const landAnim = this.selectSpineAnimation(
        spineInstance,
        "land",
        targetSymbolId,
      );
      if (landAnim) appearAnimation = landAnim;
    }

    if (spineInstance.state && appearAnimation) {
      // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
      spineInstance.skeleton.setBonesToSetupPose();
      spineInstance.state.clearTracks();
      // Play appear animation once (no loop) - will stop at last frame when finished
      spineInstance.state.addAnimation(0, appearAnimation, false, 0);
    }

    // Track this as an active animation
    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId: targetSymbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
  }

  /**
   * Replace visible symbol animations with target symbol appear animation
   * @param {number} targetSymbolId - Symbol ID to replace visible symbols with
   */
  replaceVisibleSymbols(targetSymbolId) {
    // Use GAME_LAYOUT indices to loop through visible positions
    for (const symbolIndex of GAME_LAYOUT.SYMBOL_INDICES) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) {
        continue;
      }

      // Get the pre-positioned spine instance for this position with target symbol
      const spineKey = `${symbolIndex}_${targetSymbolId}`;
      const spineInstance = this.prePositionedSpines.get(spineKey);
      if (!spineInstance) {
        continue;
      }

      // Hide any existing animation at this position
      const existingAnimation = this.activeAnimations.get(symbolIndex);
      if (existingAnimation) {
        this.hideWinAnimation(symbolIndex);
      }

      // Make spine and overlay visible
      spineInstance.alpha = 1;
      spineInstance.visible = true;
      spineInstance.autoUpdate = true;
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;

      // Play appear animation (fallback to land if appear not available)
      let appearAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        targetSymbolId,
      );

      // If no appear animation found, try land as fallback
      if (
        !appearAnimation ||
        appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name
      ) {
        const landAnim = this.selectSpineAnimation(
          spineInstance,
          "land",
          targetSymbolId,
        );
        if (landAnim) appearAnimation = landAnim;
      }

      if (spineInstance.state && appearAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, appearAnimation, true, 0);
      }

      // Track this as an active animation
      this.activeAnimations.set(symbolIndex, {
        spineInstance,
        symbolId: targetSymbolId,
        animationType: "appear",
      });

      // Show coin value text if this is a coin symbol
      this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
    }
  }

  /**
   * Create expanding wild spine instance
   */
  createExpandingWildSpine() {
    try {
      if (!this.expandingWildSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      const skeletonKey = this.expandingWildSpineData.skeleton;
      const atlasKey = this.expandingWildSpineData.atlas;

      if (!assets.has(skeletonKey) || !assets.has(atlasKey)) {
        return null;
      }

      const expandingWildSpine = Spine.from({
        skeleton: skeletonKey,
        atlas: atlasKey,
      });

      if (
        !expandingWildSpine ||
        !expandingWildSpine.skeleton ||
        !expandingWildSpine.state
      ) {
        return null;
      }

      if (
        !expandingWildSpine.skeleton.data ||
        !expandingWildSpine.skeleton.data.animations
      ) {
        return null;
      }

      return expandingWildSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Calculate responsive scale and positioning for expanding wild spine animation
   * @param {Spine} spineInstance - The spine instance to scale
   * @param {number} containerWidth - Available container width
   * @param {number} containerHeight - Available container height (full reel height)
   * @returns {Object} - Scale and positioning information
   * Cached for performance - invalidated when dimensions change
   */
  calculateExpandingWildScale(
    spineInstance,
    containerWidth,
    containerHeight,
    preserveAnimation = false,
  ) {
    // Set default animation to get proper bounds (only if not preserving current animation)
    if (
      !preserveAnimation &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.setAnimation(0, defaultAnimation.name, true);
    }

    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (
      this._expandingWildScaleCache &&
      this._expandingWildScaleCache.cacheKey === cacheKey
    ) {
      return this._expandingWildScaleCache.result;
    }

    // Calculate scale to fit within container while maintaining aspect ratio
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    // Calculate final dimensions and centering offsets
    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._expandingWildScaleCache = {
      cacheKey,
      result,
    };

    return result;
  }

  /**
   * Apply scaling and positioning to expanding wild spine
   */
  applyExpandingWildScaling() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      return;
    }

    // Get current animation before scaling (to preserve it)
    let currentAnimation = null;
    let currentLoop = false;
    if (this.expandingWildSpine.state) {
      const currentTrack = this.expandingWildSpine.state.getCurrent(0);
      if (currentTrack && currentTrack.animation) {
        currentAnimation = currentTrack.animation.name;
        currentLoop = currentTrack.loop;
      }
    }

    // Calculate full reel height (4 visible symbols)
    const fullReelHeight = this.symbolHeight * GAME_LAYOUT.SYMBOLS_VISIBLE;

    const scaleData = this.calculateExpandingWildScale(
      this.expandingWildSpine,
      this.symbolWidth,
      fullReelHeight,
      true, // Preserve animation during resize
    );

    if (this.expandingWildSpine.scale) {
      this.expandingWildSpine.scale.set(
        scaleData.scale,
        scaleData.scale * this.expandingWildHeightMultiplier,
      );
    }

    if (this.expandingWildSpine.position) {
      const yOffset = fullReelHeight * this.expandingWildYOffsetMultiplier;
      this.expandingWildSpine.position.set(scaleData.x, scaleData.y + yOffset);
    }

    // Restore the animation that was playing before resize
    if (currentAnimation && this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.setAnimation(
          0,
          currentAnimation,
          currentLoop,
        );
      } catch (error) {
        // Silent fail - animation might not exist
      }
    }
  }

  /**
   * Warm up expanding wild spine with all animations (appear, idle, win)
   * Same pattern as warmUpSpineAnimation for symbol spines
   */
  warmUpExpandingWildSpine() {
    if (!this.expandingWildSpine || !this.expandingWildSpine.state) {
      return;
    }

    const animations = ["appear", "idle", "win"];

    animations.forEach((animName) => {
      try {
        // Check if animation exists
        const hasAnim =
          this.expandingWildSpine.skeleton?.data?.animations?.find(
            (anim) => anim.name === animName,
          );

        if (!hasAnim) {
          return;
        }

        // Set animation
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.setAnimation(0, animName, false);

        // CRITICAL: Update one frame to trigger shader compilation
        this.expandingWildSpine.state.update(0.016);

        // CRITICAL: Populate bone/slot hierarchy for GPU
        try {
          if (this.expandingWildSpine.skeleton.physics !== undefined) {
            this.expandingWildSpine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip if physics not initialized
        }

        // Keep at alpha = 0.001 so PixiJS continues rendering it
        this.expandingWildSpine.alpha = 0.001;

        // Clear tracks after warmup frame renders
        requestAnimationFrame(() => {
          if (this.expandingWildSpine?.state) {
            this.expandingWildSpine.state.clearTracks();
            this.expandingWildSpine.alpha = 0.001;
          }
        });
      } catch (error) {
        // Silent fail - warmup is optional optimization
      }
    });
  }

  /**
   * Get available animation names for expanding wild spine
   */
  getExpandingWildAvailableAnimations() {
    if (!this.expandingWildSpine?.skeleton?.data?.animations) {
      return [];
    }
    return this.expandingWildSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
  }

  /**
   * Initialize expanding wild spine and add to container
   */
  initializeExpandingWildSpine() {
    if (this.expandingWildInitialized || !this.reelContainer) {
      return;
    }

    this.expandingWildSpine = this.createExpandingWildSpine();

    if (!this.expandingWildSpine) {
      return;
    }

    // Validate spine components
    const missingComponents = [];
    if (!this.expandingWildSpine.skeleton) missingComponents.push("skeleton");
    if (!this.expandingWildSpine.state) missingComponents.push("state");
    if (!this.expandingWildSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.expandingWildSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.expandingWildSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    // Apply x2 skin for middle reel (index 2) - only if multiplier feature is enabled
    if (
      this.reelIndex === 2 &&
      this.expandingWildSpine.skeleton &&
      FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED
    ) {
      try {
        const x2Skin = this.expandingWildSpine.skeleton.data.findSkin("x2");
        if (x2Skin) {
          this.expandingWildSpine.skeleton.setSkin(x2Skin);
          this.expandingWildSpine.skeleton.setSlotsToSetupPose();
        }
      } catch (error) {
        // Fail silently
      }
    }

    this.expandingWildSpine.position.set(0, 0);
    this.expandingWildSpine.zIndex = 15; // Higher than symbol animations
    this.expandingWildSpine.alpha = 0.001; // Hidden initially, keep in render pipeline
    this.isExpandingWildVisible = false;

    this.reelContainer.addChild(this.expandingWildSpine);
    this.applyExpandingWildScaling();

    if (this.expandingWildSpine.parent !== this.reelContainer) {
      return;
    }

    // Minimal warmup - warm up "in" animation since that's what plays first
    this.warmUpExpandingWildSpine();

    this.expandingWildInitialized = true;
  }

  /**
   * Clear all active timeouts for expanding wild animations
   */
  clearExpandingWildTimeouts() {
    this.expandingWildTimeouts.forEach(clearTimeout);
    this.expandingWildTimeouts = [];
  }

  /**
   * Hide expanding wild animation immediately (synchronous, same as regular win animations)
   */
  hideExpandingWildFullReel() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      this.isExpandingWildVisible = false;
      this.currentExpandingWildAnimation = null;
      return;
    }

    // Synchronous cleanup - same pattern as hideWinAnimation()
    this.expandingWildSpine.alpha = 0.001; // Keep in render pipeline
    this.expandingWildSpine.visible = false;
    this.expandingWildSpine.autoUpdate = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;

    if (this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.clearTracks();
        // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Force stop expanding wild animations (synchronous, same as regular win animations)
   */
  forceStopExpandingWildFullReel() {
    this.clearExpandingWildTimeouts();
    // Direct synchronous cleanup - same pattern as regular animations
    this.hideExpandingWildFullReel();
  }

  /**
   * Play expanding wild full-reel animation (appear animation, then idle)
   */
  playExpandingWildFullReel() {
    // Lazy initialization if not already initialized
    if (!this.expandingWildInitialized) {
      this.initializeExpandingWildSpine();
    }

    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state
    ) {
      return;
    }

    requestAnimationFrame(() => {
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.clearListeners();
        this.expandingWildSpine.state.apply(this.expandingWildSpine.skeleton);
        // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();

        this.expandingWildSpine.alpha = 1; // Show
        this.expandingWildSpine.visible = true;
        this.expandingWildSpine.autoUpdate = true;
        this.isExpandingWildVisible = true;

        // Sound is played centrally in useWinController
        // Middle reel (x2 multiplier) plays additional sound when entering idle

        // Play "appear" animation (non-looping), then transition to "idle" (looping)
        this.expandingWildSpine.state.setAnimation(0, "appear", false);
        this.expandingWildSpine.state.addAnimation(0, "idle", true, 0);
        this.currentExpandingWildAnimation = "appear";

        // Add listener for when "appear" completes and "idle" starts
        // Play multiplier sound for middle reel (reel 2 with x2 skin) - only if multiplier feature is enabled
        if (
          this.reelIndex === 2 &&
          FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED
        ) {
          const completeListener = {
            complete: (entry) => {
              if (entry.animation.name === "appear") {
                soundManager.play(SOUND_NAME.expanding_wild_multiplier);

                // Remove listener after playing once
                this.expandingWildSpine.state.removeListener(completeListener);
              }
            },
          };
          this.expandingWildSpine.state.addListener(completeListener);
        }
      } catch (error) {
        // Silent fail
        this.hideExpandingWildFullReel();
      }
    });
  }

  /**
   * Play expanding wild win animation (switches from idle to win)
   */
  playExpandingWildFullReelWin() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    this._pendingRAF = requestAnimationFrame(() => {
      this._pendingRAF = null;
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        // Switch to win animation (looping) - transitions smoothly from current animation
        this.expandingWildSpine.state.setAnimation(0, "win", true);
        this.currentExpandingWildAnimation = "win";
      } catch (error) {
        // Silent fail
      }
    });
  }

  /**
   * Play dark animation for expanding wild (indicates it's not part of current line)
   */
  playExpandingWildDarkAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to dark animation (play once, hold at last frame)
      this.expandingWildSpine.state.setAnimation(0, "dark", false);
      this.currentExpandingWildAnimation = "dark";
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Play win animation for expanding wild (restore normal appearance)
   */
  playExpandingWildWinAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to win animation (looping)
      this.expandingWildSpine.state.setAnimation(0, "win", true);
      this.currentExpandingWildAnimation = "win";
    } catch (error) {
      // Silent fail
    }
  }

  // ── The cell the player arrived on ─────────────────────────────────────
  //
  // Not a card on the board — the OPENED SCATTER with the card inside it,
  // which is what the base game leaves behind when the reveal settles. The
  // window, its frame and the package standing in it are one cell, and putting
  // only the card there loses the two thirds of it that say where the package
  // came from.
  //
  // So this is the ordinary reveal machinery, entered at the end: the same
  // pooled `scg` spine every other cell uses, the same RevealSymbolController,
  // the same config — only showSettled instead of play, because there is
  // nothing left to reveal. The player already watched it open in the base
  // game; arriving in the room they should find it exactly as they left it,
  // not be told again.
  //
  // @param {number} symbolIndex Row on this reel.
  // @param {{tier:number,value:number}} item The settled package.
  showSettledGambleScatter(symbolIndex, item) {
    const symbolId = SCATTER_2_SYMBOL_ID;
    const entry = REVEAL_SYMBOLS[symbolId];
    if (!entry || !item || typeof item.tier !== "number") return;

    const spineInstance = this.prePositionedSpines.get(
      `${symbolIndex}_${symbolId}`,
    );
    if (!spineInstance) {
      // Silent here would look identical to "the frame just is not drawn":
      // the reel's flat card texture is still underneath, so the cell shows a
      // bare card with no window round it and nothing says why.
      console.warn(
        "[FreeSpinBasicOverlayReel] No pooled scg spine for cell",
        symbolIndex,
        "on reel",
        this.reelIndex,
      );
      return;
    }

    // Already showing this exact package — rebuilding would tear down a live
    // card to put back the same one.
    //
    // But the call is still worth something: this runs again when the ROOM
    // becomes visible, and between the first mount and that moment the cell
    // can have been swept back to its resting alpha. So re-assert what makes
    // it visible, and only skip the rebuild.
    if (
      this._settledScatter?.symbolIndex === symbolIndex &&
      this._settledScatter?.tier === item.tier &&
      this._settledScatter?.value === item.value &&
      this.revealControllers?.get(symbolIndex)
    ) {
      spineInstance.alpha = 1;
      spineInstance.visible = true;
      spineInstance.autoUpdate = true;
      if (!this._revealHold) this._revealHold = new Set();
      this._revealHold.add(symbolIndex);
      const cell = this.overlaySymbolRefs[symbolIndex];
      if (cell) {
        cell.alpha = 1;
        cell.visible = true;
      }
      return;
    }

    // Take the cell over, exactly as playRevealSequence does.
    this.activeAnimations.delete(symbolIndex);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    // ── The pooled spine is ASLEEP ──
    //
    // scheduleWarmupCleanup parks every pooled spine with autoUpdate FALSE
    // three frames after it is built — the warm-up is over and a hundred idle
    // skeletons ticking for nothing is not free. Nothing ticks it again until
    // something wakes it, and showWinAnimation does exactly this on its way in.
    //
    // Without it, setAnimation writes a track that is never applied: the
    // skeleton keeps whatever pose the warm-up left, so `revealed_static`
    // never opens the doors and the frame never appears. The injected card
    // still shows, because it is a display child rather than a skeleton
    // attachment — which is why the cell looked like a bare card with no
    // window round it, and why nothing was logged.
    spineInstance.autoUpdate = true;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (overlaySymbol) {
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
    }

    if (!this.revealControllers) this.revealControllers = new Map();
    this.revealControllers.get(symbolIndex)?.destroy();

    const controller = new RevealSymbolController({
      spine: spineInstance,
      config: entry.buildConfig(this.symbolWidth, this.symbolHeight),
      cellWidth: this.symbolWidth,
      cellHeight: this.symbolHeight,
    });
    this.revealControllers.set(symbolIndex, controller);

    if (!controller.showSettled(item)) {
      controller.destroy();
      this.revealControllers.delete(symbolIndex);
      return;
    }

    // ── The pose a WON package actually rests on ──
    //
    // `revealed_static` is the pose a BLANK ends on — gambleScatterConfig says
    // so where it declares `settle`: "A WON package overrides this a moment
    // later — playRevealWin puts `win` on the same track". Every cell we ever
    // restore here is a won one, by definition: the round only exists because
    // a package was taken. So this is the same last step the live cell takes,
    // through the same method, and not a settled state reconstructed by hand.
    //
    // FROZEN on its last frame, not looped. The live cell loops it because it
    // is still waiting for the feature to take the screen; this one is not
    // waiting for anything — the round is already being played — so it holds
    // the pose and drops the bounce. See restOnWinEnd.
    controller.restOnWinEnd();

    // Applied now rather than on the next tick, so the very first frame the
    // player sees is the open window — not one frame of the warm-up's pose.
    try {
      spineInstance.state.update(0);
      spineInstance.state.apply(spineInstance.skeleton);
      spineInstance.skeleton.updateWorldTransform(
        spineInstance.skeleton.physics !== undefined ? 2 : 0,
      );
    } catch {
      try {
        spineInstance.skeleton.updateWorldTransform();
      } catch {}
    }

    // ── RE-FIT, now that the window is in the pose it will rest in ──
    //
    // RevealSymbolController cancels the host's bone chain by MEASURING it:
    //
    //   const boneScale = Math.hypot(bone.a, bone.c);
    //   this.fit.scale.set(1 / (boneScale * spineScale));
    //
    // showSettled runs that before it plays anything, so the measurement is
    // taken on whatever pose the pooled spine happened to be parked in. Then
    // restOnWinEnd puts `win` on and the forced update above moves
    // `symbol_placeholder` to its resting scale — and the card is left wearing
    // a correction computed for a different pose. That is the whole reason it
    // came out smaller here than in the base game, where the reveal lands the
    // card with the window already open.
    //
    // One line, and it needs no tuning: the fit is derived from the skeleton,
    // so measuring it at the right moment gives the base game's size exactly.
    controller._applyFit?.();

    // Held for as long as it is displayed, not for a sequence — this cell is
    // standing still on purpose until hideSettledGambleScatter takes it off.
    if (!this._revealHold) this._revealHold = new Set();
    this._revealHold.add(symbolIndex);

    this._settledScatter = { symbolIndex, tier: item.tier, value: item.value };
  }

  /**
   * The live card standing on this reel, if the settled scatter is here.
   *
   * The entry flourish needs it to read pip positions and to put each pip out
   * as it is spent — see playEntrySequence.
   *
   * @returns {GambleLevelOverlay|null}
   */
  settledGambleCard() {
    const at = this._settledScatter?.symbolIndex;
    if (at === undefined || at === null) return null;
    return this.revealControllers?.get(at)?._landedOverlay ?? null;
  }

  /** Take the settled scatter off, and hand the cell back to the pool. */
  hideSettledGambleScatter() {
    const at = this._settledScatter?.symbolIndex;
    this._settledScatter = null;
    if (at === undefined || at === null) return;
    this._revealHold?.delete(at);
    this.releaseRevealSymbol(at, SCATTER_2_SYMBOL_ID);
  }

  cleanup() {
    // Cancel any pending requestAnimationFrame
    if (this._pendingRAF) {
      cancelAnimationFrame(this._pendingRAF);
      this._pendingRAF = null;
    }

    this.hideAllAnimations();
    this.hideExpandingWildFullReel();
    this.clearExpandingWildTimeouts();
    this.hideSettledGambleScatter();

    // Cleanup all coin value texts
    this.hideAllCoinValueTexts();

    for (const spineInstance of this.prePositionedSpines.values()) {
      // `destroyed` guard: the room's subtree is unmounted on the way out, so
      // the parent may already have destroyed these — see WinTextController.
      if (spineInstance && !spineInstance.destroyed) {
        if (spineInstance.state) spineInstance.state.clearTracks();
        if (spineInstance.parent) {
          spineInstance.parent.removeChild(spineInstance);
        }
        spineInstance.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }

    // Cleanup expanding wild spine
    if (this.expandingWildSpine && !this.expandingWildSpine.destroyed) {
      try {
        if (this.expandingWildSpine.state) {
          this.expandingWildSpine.state.clearTracks();
          this.expandingWildSpine.state.clearListeners();
        }

        if (this.expandingWildSpine.parent) {
          this.expandingWildSpine.parent.removeChild(this.expandingWildSpine);
        }

        this.expandingWildSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.prePositionedSpines.clear();
    this.activeAnimations.clear();
    this.animationCache.clear();
    this.animationAvailability.clear();
    this.coinValueContainers.clear();
    this.expandingWildSpine = null;
    this.expandingWildInitialized = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;
    this.destroyRevealSymbols();
  }

  // ── Reveal symbols ────────────────────────────────────────────────────────
  //
  // Some symbols are not a single clip — they land, open, spin a strip of
  // other symbols inside themselves, and settle on a result. All of that lives
  // in RevealSymbolController; this file only looks the symbol up and hands
  // off, because the bonus game needs the same behaviour with different
  // artwork and neither belongs in the overlay reel.

  /** True when this symbol runs a reveal sequence instead of a plain clip. */
  hasRevealSequence(symbolId) {
    return !!REVEAL_SYMBOLS[symbolId];
  }

  /**
   * Run a symbol's full reveal sequence and resolve once it has settled.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {number} symbolId
   * @param {*}      result      Item the inner strip must land on.
   */
  async playRevealSequence(symbolIndex, symbolId, result, configOverride = null) {
    const entry = REVEAL_SYMBOLS[symbolId];
    // Reports whether the sequence actually RAN. A caller that has to know
    // afterwards — the `+FS` walk marks the cells whose window opened — cannot
    // tell from the promise alone, because an async function resolves just the
    // same when it returned at a guard.
    if (!entry) return false;

    // ── The HOST may not be the symbol ──
    //
    // A `+FS` scatter reveals inside SC1's window, so its entry names `scg` as
    // the host and the pooled spine is looked up under THAT id. The golden
    // hosting below follows the same id, or the sequence would be moved into
    // the window under one id and put back under another.
    const hostSymbolId = entry.hostSymbolId ?? symbolId;

    const spineInstance = this.prePositionedSpines.get(
      `${symbolIndex}_${hostSymbolId}`,
    );
    if (!spineInstance) return false;

    // Take this cell over. Any clip the win display started must not fight the
    // sequence for track 0, and the overlay must stay visible throughout.
    this.activeAnimations.delete(symbolIndex);

    // ── The pose it appears IN ──
    //
    // TURBO IS THE CASE THIS EXISTS FOR. Landing is skipped outright there —
    // useLandingAnimations shows the static texture and `continue`s — so
    // nothing has touched this pooled spine since the LAST round used it. Made
    // visible as it stands, the first frame the renderer paints is whatever
    // pose it was left in: for a mystery that is the end of the previous
    // `reveal`, doors open and frame faded, sitting over a closed-door
    // texture. That is the flash.
    //
    // In normal play it never showed, because showLandingAnimation ran first
    // and it does exactly this — reset, queue, force the frame, THEN show.
    // "Make visible AFTER animation is queued — prevents stale-pose flash" is
    // its own comment, and the reveal was the one entry point that skipped it.
    //
    // The SETUP pose is the right one to appear in: for both reveal hosts it is
    // the door SHUT, which is what the reel's static texture underneath is
    // already showing. So the spine becoming visible changes nothing on screen,
    // and the sequence starts from a known state one frame later.
    spineInstance.state?.clearTracks();
    spineInstance.skeleton?.setToSetupPose?.();
    try {
      spineInstance.skeleton?.updateWorldTransform(
        spineInstance.skeleton.physics !== undefined ? 2 : 0,
      );
    } catch {
      try {
        spineInstance.skeleton?.updateWorldTransform();
      } catch {}
    }

    // ── A reveal on a GOLDEN cell plays inside the golden box ──
    //
    // The pooled spine is positioned against the whole cell in this overlay's
    // container, which makes it a SIBLING of the Golden Mystery's frame rather
    // than something standing in its window. Left there it covers the frame
    // edge to edge, and the scatter reads as climbing out of the golden box to
    // stand on top of it.
    //
    // So when the row is running the feature, the spine is moved into the
    // frame's own window for the duration. Its parent and place are put back by
    // releaseRevealSymbol, which is the one exit every path already goes
    // through.
    this._hostRevealInGolden(symbolIndex, spineInstance, hostSymbolId);

    spineInstance.alpha = 1;
    spineInstance.visible = true;
    // ── WAKE IT. The sequence cannot run on a sleeping skeleton ──
    //
    // scheduleWarmupCleanup parks every pooled spine with autoUpdate FALSE.
    // Every other entry point here sets it back to true; this one did not, and
    // got away with it only because useLandingAnimations had already bounced
    // the cell — showLandingAnimation wakes the spine as a side effect. The
    // mystery's own config even documents the dependency, leaving `land` empty
    // "because useLandingAnimations bounces every landed symbol".
    //
    // TURBO DOES NOT BOUNCE. Landing is skipped outright for ordinary symbols
    // (`showSymbolTexture` then `continue`), so nothing ever wakes the spine —
    // `anticipation` never advances, never reaches a loop boundary, and the
    // `complete` that _waitForLoopCycle is waiting for can never fire. The
    // sequence hangs forever holding criticalAnimationPlaying, which is the
    // hard stop on landing a mystery with the spacebar held.
    spineInstance.autoUpdate = true;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (overlaySymbol) {
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
    }

    if (!this.revealControllers) this.revealControllers = new Map();
    this.revealControllers.get(symbolIndex)?.destroy();

    const controller = new RevealSymbolController({
      spine: spineInstance,
      // The override is how a caller varies ONE round without a second config:
      // the mystery picks its anticipation length from what the round actually
      // pays — see Games/Shared/mysteryDrama.
      config: {
        ...entry.buildConfig(this.symbolWidth, this.symbolHeight),
        ...(configOverride || {}),
      },
      cellWidth: this.symbolWidth,
      cellHeight: this.symbolHeight,
    });
    this.revealControllers.set(symbolIndex, controller);

    // Held only while the sequence is actually running — see the sweep in
    // hideAllAnimations for why the window has to be this narrow.
    if (!this._revealHold) this._revealHold = new Set();
    this._revealHold.add(symbolIndex);
    try {
      await controller.play(entry.toStripItem(result));
    } finally {
      this._revealHold.delete(symbolIndex);
    }
    return true;
  }

  /**
   * Make a landed reveal symbol celebrate.
   *
   * Separate from playRevealSequence because the timing is not this symbol's
   * to choose: the whole trigger line celebrates together once the strip has
   * landed, so the win controller calls this at the same moment it starts the
   * other symbols' win animations.
   *
   * @param {number} symbolIndex Row on the reel.
   * @returns {boolean} false when this cell has no live reveal.
   */
  playRevealWin(symbolIndex) {
    return this.revealControllers?.get(symbolIndex)?.playWin() ?? false;
  }

  /**
   * The static reel texture a settled reveal must leave behind, if any.
   *
   * A reveal runs up here on the overlay, while the reel's own texture below
   * it is still whatever landed. When the two disagree — a blank result over
   * an unopened `scg` — restoring the statics shows the wrong symbol. The
   * config decides what the cell should hold; the CALLER owns the reel and
   * performs the swap, because this class has no reel controller.
   *
   * @param   {number} symbolId The host symbol that ran the reveal.
   * @param   {*}      result   Round payload, same value playRevealSequence got.
   * @returns {{symbolId:number,label:Object|null}|null} what the cell must
   *          hold, or null to leave it alone.
   */
  getRevealStaticCell(symbolId, result) {
    const entry = REVEAL_SYMBOLS[symbolId];
    if (!entry?.toStaticCell) return null;
    return entry.toStaticCell(entry.toStripItem?.(result) ?? result);
  }

  /**
   * Play one clip on a landed symbol and resolve when it finishes.
   *
   * Written for the scatter's `distribute` — the flourish it plays before its
   * trail leaves for the level box — but it names no clip, so anything the
   * skeleton carries works.
   *
   * `syncWinAnimation` was the near-miss: it plays a clip on a pooled spine
   * but tells nobody when it is done, and this sequence has to hold the UI
   * lock until the animation has actually finished. Hence the promise.
   *
   * Resolves immediately when the cell has no such clip, so a missing
   * animation skips its beat instead of hanging the round.
   *
   * @param   {number} symbolIndex   Row on the reel.
   * @param   {number} symbolId
   * @param   {string} animationType Clip to look up, e.g. "distribute".
   * @returns {Promise<void>}
   */
  playSymbolClipOnce(symbolIndex, symbolId, animationType) {
    const spineInstance = this.prePositionedSpines.get(
      `${symbolIndex}_${symbolId}`,
    );
    if (!spineInstance?.state) return Promise.resolve();

    const name = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId,
    );
    // A substring match can resolve to something else entirely — only play it
    // when the skeleton really has this clip.
    if (!name || name.toLowerCase() !== animationType.toLowerCase()) {
      return Promise.resolve();
    }

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (overlaySymbol) {
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
    }
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;

    return new Promise((resolve) => {
      const entry = spineInstance.state.setAnimation(0, name, false);
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        spineInstance.state.removeListener(listener);
        resolve();
      };
      const listener = {
        complete: (completed) => {
          if (completed === entry) finish();
        },
        // A clip that is replaced or interrupted must not leave the sequence
        // waiting for a `complete` that will never arrive.
        end: (ended) => {
          if (ended === entry) finish();
        },
      };
      spineInstance.state.addListener(listener);
    });
  }

  /**
   * A landed cell's centre, in another container's space.
   *
   * The trail starts at the symbol and ends at a box on the scene spine, and
   * the two live in different parents — so the point has to go through global
   * on the way. Same conversion useLandingAnimations does for the pots.
   *
   * @param   {number} symbolIndex
   * @param   {PIXI.Container} targetContainer Space to express the point in.
   * @returns {{x:number,y:number}|null}
   */
  symbolCenterIn(symbolIndex, targetContainer) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol || !targetContainer || targetContainer.destroyed) {
      return null;
    }
    const globalPos = overlaySymbol.toGlobal(_levelTrailPoint.set(0, 0));
    const local = targetContainer.toLocal(globalPos);
    return { x: local.x, y: local.y };
  }

  /**
   * Take a settled reveal off a cell and hand the cell back to the reels.
   *
   * A reveal ends frozen on its last frame — for the mystery that frame is
   * fully transparent, so the cell LOOKS finished while the spine is still
   * sitting on it holding an injected symbol and owning track 0. Nothing else
   * can use the cell in that state: the win display would celebrate a symbol
   * that is covered by a picture of itself.
   *
   * So the reveal is torn down and its host spine returned to the pool exactly
   * as it was found — no tracks, setup pose, hidden. The caller has already
   * given the cell its real symbol, so from here it is an ordinary cell and the
   * ordinary win presentation runs on it.
   *
   * Only for reveals that leave nothing behind. SC1 must NOT be released: its
   * open window with the awarded package inside it IS the settled cell.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {number} symbolId    The host symbol that ran the reveal.
   */
  /**
   * Move a reveal's pooled spine into the Golden Mystery's window, if this row
   * is running one. Remembers where it came from so it can be put back.
   *
   * Position is zeroed rather than kept: inside the window the origin IS the
   * cell's centre, which is exactly where the pooled spine was aiming in the
   * container's coordinates. Scale is untouched — the frame's `fit` cancels the
   * bone chain, so the window measures in the same pixels the pool does.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {object} spineInstance The pooled spine about to play.
   * @param {number} symbolId Which pooled spine this is — remembered so the
   *        teardown can hand the right one back. See the record below.
   */
  _hostRevealInGolden(symbolIndex, spineInstance, symbolId) {
    const host = this.goldenControllers?.get(symbolIndex)?.revealHost?.();
    if (!host || spineInstance.parent === host) return;
    if (!this._revealHosted) this._revealHosted = new Map();
    // Only the FIRST host is remembered. A second sequence on the same cell
    // must go back to the container, never to the window it is standing in.
    if (!this._revealHosted.has(symbolIndex)) {
      this._revealHosted.set(symbolIndex, {
        parent: spineInstance.parent,
        x: spineInstance.x,
        y: spineInstance.y,
        // Which pooled spine this is. Kept because clearGoldenMysteries must
        // hand the REVEAL back BEFORE it destroys the frame — the spine is a
        // child of the frame's window by then, and the pool is keyed by symbol
        // id. Without it the pooled scg spine is destroyed with the window.
        symbolId,
      });
    }
    spineInstance.x = 0;
    spineInstance.y = 0;
    host.addChild(spineInstance);
  }

  /**
   * Put a hosted reveal spine back where the pool left it.
   *
   * Called on the way out, and unconditionally: the golden frame it was living
   * in is destroyed with its controller, so a spine left parented to it would
   * go down with it and the pool would hand out a dead object next round.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {object} spineInstance The pooled spine being handed back.
   */
  _unhostReveal(symbolIndex, spineInstance) {
    const home = this._revealHosted?.get(symbolIndex);
    if (!home) return;
    this._revealHosted.delete(symbolIndex);
    spineInstance.x = home.x;
    spineInstance.y = home.y;
    home.parent?.addChild(spineInstance);
  }

  releaseRevealSymbol(symbolIndex, symbolId) {
    const controller = this.revealControllers?.get(symbolIndex);
    if (controller) {
      controller.destroy();
      this.revealControllers.delete(symbolIndex);
    }

    const spineInstance = this.prePositionedSpines.get(
      `${symbolIndex}_${symbolId}`,
    );
    if (!spineInstance) return;

    // Home first: the window it may be standing in belongs to a Golden cell
    // whose controller is destroyed on the same call as this one.
    this._unhostReveal(symbolIndex, spineInstance);

    // Hidden BEFORE the pose is reset, not after. Clearing the tracks and
    // restoring the setup pose puts the mystery back to fully opaque — frame,
    // doors and every light — so a spine that is still at alpha 1 while that
    // happens is one mis-ordered line away from flashing the whole symbol back
    // onto the board.
    spineInstance.alpha = 0.001;
    spineInstance.visible = false;
    // Back to sleep. playRevealSequence woke it (autoUpdate = true) because a
    // sequence cannot run on a parked skeleton; handing the cell back has to
    // undo that, or the spine keeps ticking for nothing until some later
    // hideAllAnimations sweep happens to catch it.
    spineInstance.autoUpdate = false;
    spineInstance.state?.clearTracks();
    spineInstance.skeleton?.setBonesToSetupPose();
    spineInstance.skeleton?.setSlotsToSetupPose();
  }

  // ── Golden Mystery (B2) ───────────────────────────────────────────────────
  //
  // A Mystery that reveals into B2 does not settle on a symbol — the cell turns
  // into a respin. Three layers do it, all in this one cell:
  //
  //   `my`  the frame, opened with `reveal_spin` and then chasing its lights
  //   `myg` a LAYER above it (z 50 against 45) whose `reveal` booms itself away
  //   the track, spinning inside `myg`'s slot
  //
  // Held in its own map rather than `revealControllers`, because it is not a
  // reveal that settles: it must not be reachable through anything that walks
  // the reveal controllers. It is torn down in exactly one place — spin start,
  // through clearGoldenMysteries.
  //
  // (It used to say "through the settled-reveal guards in showWinAnimation /
  // syncWinAnimation". Those guards are gone — see the note on hasGoldenMystery
  // for how a Golden cell is kept out of the ordinary win path now.)

  /**
   * Run one Golden cell: build-up, doors, lid, track, landing.
   *
   * @param   {number} symbolIndex Row on the reel.
   * @param   {Object} p
   * @param   {{symbolId:number,value:number}} p.outcome What the server landed.
   * @param   {Array}  p.items     The pool the track spins.
   * @param   {string} p.anticipationClip
   * @returns {Promise<void>}
   */
  async playGoldenMysterySequence(
    symbolIndex,
    { outcome, items, anticipationClip, stopOrder, onRevealed } = {},
  ) {
    // ── ONE pooled spine, not two ──
    //
    // The golden layer is not a second spine over the cell: it lives INSIDE the
    // mystery's window, injected into `attachment_slot_symbol` like any other
    // slot object, and the controller builds it. Only the frame comes from the
    // pool.
    // ── The frame is the OLD rig, not the mystery the player landed ──
    //
    // symbolData[9] is the LID: doors only, no frame, no lights. Opening the
    // golden on it would part the doors onto a bare cell.
    // GOLDEN_FRAME_SYMBOL_ID is `myg` itself — its own frame, its own lights,
    // its own window — pooled per cell so it is ready before the lid opens.
    // See SymbolData.
    const frameSpine = this.prePositionedSpines.get(
      `${symbolIndex}_${GOLDEN_FRAME_SYMBOL_ID}`,
    );
    if (!frameSpine) {
      console.warn(
        "[GoldenMystery] No pooled mystery spine on reel",
        this.reelIndex,
        "row",
        symbolIndex,
      );
      return;
    }

    // Take the cell over from the win display, and start BOTH hosts from a
    // known pose — the same discipline playRevealSequence follows, and for the
    // same reason: turbo skips landing, so nothing has touched these spines
    // since the last round used them.
    this.activeAnimations.delete(symbolIndex);
    frameSpine.state?.clearTracks();
    frameSpine.skeleton?.setToSetupPose?.();
    try {
      frameSpine.skeleton?.updateWorldTransform(
        frameSpine.skeleton.physics !== undefined ? 2 : 0,
      );
    } catch {
      try {
        frameSpine.skeleton?.updateWorldTransform();
      } catch {}
    }
    frameSpine.autoUpdate = true;
    frameSpine.alpha = 1;
    frameSpine.visible = true;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (overlaySymbol) {
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
    }

    if (!this.goldenControllers) this.goldenControllers = new Map();
    this.goldenControllers.get(symbolIndex)?.destroy();

    // ── The LID: the ordinary mystery the player actually landed ──
    //
    // `my`, from the same pool, sitting above the frame. It plays the
    // build-up and the doors and then fades, uncovering the frame below — so a
    // golden cell opens with the SAME doors as every other mystery on the
    // board. Before this the frame opened its own, which were the old rig's.
    //
    // Null is tolerated: the controller falls back to the frame opening itself,
    // which is exactly what it did before.
    const lidSpine =
      this.prePositionedSpines.get(`${symbolIndex}_${MYSTERY_SYMBOL_ID}`) || null;
    if (lidSpine) {
      lidSpine.state?.clearTracks();
      lidSpine.skeleton?.setToSetupPose?.();
      lidSpine.autoUpdate = true;
      lidSpine.alpha = 1;
      lidSpine.visible = true;
    }

    const controller = new GoldenMysteryCellController({
      frameSpine,
      lidSpine,
      makeGolden: buildGoldenMysterySpine,
      // For the moments a flat cell cannot act: a multiplier firing, a collector
      // gathering, an upgrade bursting. See playCellSpine.
      makeFeatureSpine: buildFeatureSpine,
      // The cell's own overlay container, purely so the controller can find the
      // flat layer above it — the one place a multiplier's trail can cross the
      // board. See GoldenMysteryCellController.trailLayer.
      cellContainer: overlaySymbol,
      // The room's own pool: the gamble scatter cannot drop in here and the
      // +FS scatter can. See GOLDEN_STRIP_ITEMS_FREE_SPINS.
      config: buildGoldenMysteryConfig({ freeSpins: true }),
      cellWidth: this.symbolWidth,
      cellHeight: this.symbolHeight,
      // This room's trace, not the base game's — see the constructor.
      diag: fsDiag,
    });
    this.goldenControllers.set(symbolIndex, controller);

    // ── Exempt for the WHOLE feature, not just the sequence ──
    //
    // `myg`'s lid is held open by a pinned track entry. The orphan sweep clears
    // tracks on any awake spine it cannot explain, and clearing that one snaps
    // the golden sign back at full alpha over the track. So the row stays in
    // _revealHold until spin start, unlike a reveal sequence which releases in
    // its `finally`.
    if (!this._revealHold) this._revealHold = new Set();
    this._revealHold.add(symbolIndex);

    await controller.play({
      outcome,
      items,
      anticipationClip,
      stopOrder,
      onRevealed,
    });
  }

  /**
   * The Golden cell on this row, for the loop to act on.
   *
   * @param   {number} symbolIndex
   * @returns {GoldenMysteryCellController|null}
   */
  goldenCell(symbolIndex) {
    return this.goldenControllers?.get(symbolIndex) ?? null;
  }

  /**
   * True when this row BELONGS to the Golden Mystery — running it, or finished
   * with it and left as the feature's own flat picture.
   *
   * Every ordinary presentation call for the row is guarded on this, and the
   * reason is in showWinAnimation: a Golden cell's sprite still claims symbol
   * 9, because the board was masked and a B2 cell is deliberately never given a
   * new identity. So anything that resolves a spine from the cell's id lands on
   * the MYSTERY — and the first thing most of them do is shut its doors.
   *
   * The released half is not an afterthought. A cell that did not pay hands its
   * spine back at settle and keeps a flat composite; that composite is correct
   * and finished, and the win presentation must leave it alone. Testing only
   * `goldenControllers` let those cells through the moment they were released,
   * and every one of them was then animated as a closed mystery over a texture
   * the same pass had just hidden.
   */
  hasGoldenMystery(symbolIndex) {
    return (
      !!this.goldenControllers?.has(symbolIndex) ||
      !!this._goldenStatic?.has(symbolIndex)
    );
  }

  /**
   * Rows that are a FINISHED Golden picture and nothing else.
   *
   * ── The arrival board is a photograph ──
   *
   * The room opens on the base game's screen, and when that screen carried a
   * Golden Mystery the cells are painted over as flat composites — the coin
   * with its value, the magnet with its total — by the arrival block in
   * FreeSpinBasicReel. That is the whole picture: those cells were revealed,
   * collected and paid in the base game, and in here they are scenery.
   *
   * But the reel still holds MYSTERY_SYMBOL_ID at them, because a Golden cell
   * is deliberately never given a new identity. So every ordinary presentation
   * path resolved a spine from that id, found the MYSTERY, and played its
   * landing and idle over the composite — the room opened on a board of
   * question marks animating over the coins the player had just won.
   * Vladimir, 2026-09-22: "it plays mystery animation which we do not need at
   * all... we literally need them all as static textures."
   *
   * `hasGoldenMystery` is already the guard on all seven of those paths, so
   * marking the rows here is the whole fix — nothing new has to be refused in
   * seven places.
   *
   * ── Not the gamble scatter ──
   *
   * That one cell IS a spine, and has to be: it fires its pips into the level
   * box. It is excluded by the caller and owned by showSettledGambleScatter.
   *
   * @param {number[]} rows Rows on this reel that hold a settled Golden cell.
   */
  markGoldenStatic(rows) {
    if (!Array.isArray(rows) || !rows.length) return;
    if (!this._goldenStatic) this._goldenStatic = new Set();
    for (const row of rows) this._goldenStatic.add(row);
  }

  /**
   * Give those rows back to the ordinary presentation.
   *
   * Called when the arrival board goes — the first spin of the round — because
   * from then on the reel really is holding whatever it lands, and a row still
   * marked here would never animate again.
   */
  clearGoldenStatic() {
    this._goldenStatic?.clear();
  }

  /**
   * Put ONE Golden cell to sleep, mid-round, without giving it up.
   *
   * ── Park, do not release ──
   *
   * When the feature ends, only the cells the CURRENT line is lighting should
   * glow. The rest must read as ordinary board symbols so the dim covers them,
   * because a live Golden cell draws on the overlay layer, above the dim, and
   * would otherwise stay lit through every other line's turn.
   *
   * But "the rest" changes with every line. This used to DESTROY the cells that
   * did not pay, which decided for ever something that was only true for one
   * line — and the scatters the feature dropped are exactly that case: not
   * money, so not paying, so destroyed, so unable to celebrate when the trigger
   * line came round. They sat dark while the board's own scatter bounced.
   *
   * Parked instead, the cell keeps everything and can be woken by name. The
   * reel's identical composite stands in for it meanwhile — see setCellVisible.
   * Nothing is leaked: clearGoldenMysteries hands every cell back at spin
   * start, whether it was ever woken or not.
   *
   * @param {number} symbolIndex Row on the reel.
   */
  parkGoldenCell(symbolIndex) {
    const controller = this.goldenControllers?.get(symbolIndex);
    if (!controller) return;
    // ── NEVER mid-sequence ──
    //
    // Parking sets autoUpdate false, and a spine that is not ticking cannot
    // reach the end of a clip: the `complete` the cell is awaiting never
    // arrives, its promise never settles, and the whole feature stops with the
    // doors still shut. That is the same trap the orphan sweep documents for
    // reveals, and this walked straight into it — a win-cycle sweep could fire
    // while cells were still opening and freeze them where they stood.
    //
    // So a cell is parkable only once the feature has SAID it is finished with
    // it. Until then every caller here is refused, whatever it thinks it knows.
    if (!this._goldenSettled?.has(symbolIndex)) return;
    controller.stopWin?.();
    controller.setCellVisible?.(false);
  }

  /**
   * The feature is done with this cell: from now on the win cycle owns it.
   *
   * Called once per Golden cell, from the settle that paints its flat
   * composite. Before this, nothing may park the cell — see parkGoldenCell.
   *
   * @param {number}  symbolIndex Row on the reel.
   * @param {boolean} [park] Park it immediately. True for a cell no win is
   *        lighting, so the board reads correctly the moment the feature ends
   *        rather than a line-change later.
   */
  settleGoldenCell(symbolIndex, park = false) {
    if (!this.goldenControllers?.has(symbolIndex)) return;
    if (!this._goldenSettled) this._goldenSettled = new Set();
    this._goldenSettled.add(symbolIndex);
    if (park) this.parkGoldenCell(symbolIndex);
  }

  /** Give every Golden cell back. Called at spin start. */
  clearGoldenMysteries() {
    if (!this.goldenControllers?.size) return;
    for (const [symbolIndex, controller] of this.goldenControllers) {
      // ── The hosted reveal comes home FIRST ──
      //
      // A gamble scatter that opened inside this cell was reparented into the
      // frame's window (see _hostRevealInGolden), and that window is
      // `frameHolder`, which destroy() takes down WITH ITS CHILDREN. Destroy
      // first and the POOLED scg spine dies with it — the pool hands out a dead
      // object next round, and that cell can never reveal a gamble scatter
      // again for the rest of the session.
      //
      // releaseRevealSymbol calls _unhostReveal, which puts it back in this
      // overlay's container at the place the pool left it.
      const hostedId = this._revealHosted?.get(symbolIndex)?.symbolId;
      if (hostedId !== undefined && hostedId !== null) {
        this.releaseRevealSymbol(symbolIndex, hostedId);
      }
      controller.destroy();
      this._revealHold?.delete(symbolIndex);
      this.activeAnimations.delete(symbolIndex);
      // The frame goes back to the pool as it was found: asleep, hidden,
      // pristine. The lid was built by the controller and died with it.
      this.releaseRevealSymbol(symbolIndex, GOLDEN_FRAME_SYMBOL_ID);
      // The LID goes back too. It was faded to nothing during the reveal, and
      // releaseRevealSymbol is what restores the pose and puts it to sleep — so
      // without this the next mystery on this row inherits an invisible lid.
      this.releaseRevealSymbol(symbolIndex, MYSTERY_SYMBOL_ID);
    }
    this.goldenControllers.clear();
    this._goldenSettled?.clear();
  }

  /** Tear down every live reveal sequence. Safe to call repeatedly. */
  destroyRevealSymbols() {
    if (this.goldenControllers) {
      for (const controller of this.goldenControllers.values()) controller.destroy();
      this.goldenControllers.clear();
    }
    if (!this.revealControllers) return;
    for (const controller of this.revealControllers.values()) {
      controller.destroy();
    }
    this.revealControllers.clear();
  }
}

export default FreeSpinBasicOverlayReelController;
